'use client';

import { getDictionary } from '@/app/dictionaries/getDictionary';
import { AgentLogger } from '@/utils/logger-agent'; // New import
import { sendErrorToSlack } from '@/utils/slack-error-reporter';
import { Chat } from '@/utils/types';
import { XMarkIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdditionalInfoForm from './AdditionalInfoForm';
import { AddressForm } from './AddressForm';
import { getAgencyConfig } from './agency-config-helper'; // Import AgencyConfiguration
import ContactForm from './ContactForm';
import { IntroForm } from './IntroForm';
import MapTimeframeForm from './MapTimeframeForm';
import PropertyDetailsForm from './PropertyDetailsForm';
import PropertyTransactionForm from './PropertyTransactionForm';
import QuitReasonModal from './QuitReasonModal';
import ReasoningLoader from './ReasoningLoader';
import RentForm from './RentForm';
import ValuationForm from './ValuationForm';

// Device detection utilities
const detectDeviceType = (): 'mobile' | 'desktop' => {
  return window.innerWidth <= 768 ? 'mobile' : 'desktop';
};

const detectBrowser = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome')) return 'chrome';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'safari';
  if (userAgent.includes('Firefox')) return 'firefox';
  if (userAgent.includes('Edge')) return 'edge';
  return 'other';
};

const getParentUrl = (): string => {
  try {
    // Get URL from parent window (where widget is embedded)
    return window.parent.location.href;
  } catch (error) {
    // Fallback to current URL if cross-origin
    return window.location.href;
  }
};

const getTrackingParams = (url: string): Record<string, string> => {
  try {
    const urlObj = new URL(url);
    const trackingParams: Record<string, string> = {};
    
    // Standard UTM parameters
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlObj.searchParams.get(param);
      if (value) trackingParams[param] = value;
    });
    
    // Google and Facebook click IDs
    ['gclid', 'fbclid'].forEach(param => {
      const value = urlObj.searchParams.get(param);
      if (value) trackingParams[param] = value;
    });
    
    return trackingParams;
  } catch (error) {
    return {};
  }
};

// Move this function inside the component so it can access state
const captureLeadSourceData = (leadSourceDataFromWidget: any) => {
  // If we have lead source data from the widget loader, use it (it has the correct parent URL)
  if (leadSourceDataFromWidget) {
    return leadSourceDataFromWidget;
  }
  
  // Fallback to our own detection if widget loader data is not available
  const deviceType = detectDeviceType();
  const browser = detectBrowser();
  const sourceUrl = getParentUrl();
  const trackingParams = getTrackingParams(sourceUrl);
  
  // Determine utmSource based on click IDs
  let utmSource = trackingParams.utm_source;
  let utmMedium = trackingParams.utm_medium;
  const utmCampaign = trackingParams.utm_campaign;
  const utmTerm = trackingParams.utm_term;
  const utmContent = trackingParams.utm_content;
  
  // Override with click ID data
  if (trackingParams.gclid) {
    utmSource = 'google';
    utmMedium = 'cpc';
    // Could potentially extract campaign info from gclid in the future
  } else if (trackingParams.fbclid) {
    utmSource = 'facebook';
    utmMedium = 'cpc';
    // Could potentially extract campaign info from fbclid in the future
  }
  
  return {
    deviceType,
    browser,
    sourceUrl,
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
    gclid: trackingParams.gclid,
    fbclid: trackingParams.fbclid
  };
};

// Define the updated sequence of steps for the progress bar
const AVM_STEPS = [
  'sales-avm-step1-intro',
  'sales-avm-step2-address',
  'sales-avm-step3-statbel',
  'sales-avm-step4-transaction',
  'sales-avm-step5-additional-info',
  'sales-avm-step6-map-timeframe',
  'sales-avm-step7-contact',
  'sales-avm-step8-valuation',
  'sales-avm-step9-final-evaluation',
];
const TOTAL_AVM_STEPS = AVM_STEPS.length;

type Message = {
  role: 'user' | 'assistant' | 'system';
  content: string;
  followUpQuestions?: string[];
  chatId?: string;
  createdAt?: Date;
  userId?: string;
  classification?: {
    primary_intent: string;
    workflowStep?: string;
  };
  workflowStep?: string;
  id?: string;
  properties?: { 
    city?: string;
    propertyType?: string;
    livingArea?: number;
    bedrooms?: number;
    bathrooms?: number;
    plotArea?: number;
    [key: string]: any;
  };
};

interface ChatConfig {
  agencyId?: string;
  agentId?: string;
  backgroundColor?: string;
  faviconUrl?: string;
  logoUrl?: string;
  enableMfa?: boolean;
  agencyEmail?: string;
  agencyName?: string;
  agencyLanguage?: string;
  language?: string;
  textStyle?: React.CSSProperties;
  defaultLocale?: 'nl' | 'fr' | 'en';
  styling?: React.CSSProperties;
  startOpen?: boolean;
  // agency configuration
  top?: React.ComponentType<any>;
  left?: React.ComponentType<any>;
  widgetTitleOverrideKey?: string;
  headerTitleOverrideKey?: string;
  // Other configuration
  options?: {
    animationDuration?: number;
  };
  closedWidgetStyling?: React.CSSProperties; // Add optional styling for closed widget
  swapLogoWithAI?: boolean; // Add flag for swapping logo
  swapLogoWithArrow?: boolean; // Add flag for swapping logo and arrow positions
  closedWidgetCtaConfig?: { // Add config for CTA button
    useIcon?: boolean;
    colorClassName?: string;
    AIwhite?: boolean;
  };
  headerStyling?: { // Add config for header styling
    type?: 'gradient' | 'corner';
    backgroundColor?: string;
    textColor?: string;
    background?: string;
    backgroundBlendMode?: string;
  };
  closedWidgetCloseButtonClassName?: string; // Add optional className for close button
  closedWidgetCloseIconClassName?: string; // Add optional className for close icon
  preventRefreshOnUrlChange?: boolean; // Add optional flag for preventing refresh
  enableEmailCollection?: boolean; // Add optional flag for email collection
  enableEmailCollectionOnlyAgency?: boolean; // Add optional flag for agency-only email collection
  customEmailMessage?: string; // Add custom email message
  customEmailPrompt?: string; // Add custom email prompt
  customThankYouMessage?: string; // Add custom thank you message
  aiChipTextOverrideKey?: string; // Add optional key for AI chip text override
  contactPromptOverrideKey?: string;
  excludedTimeframeOptions?: ('now' | '6m' | '12m' | 'curious' | 'no')[];
  enableTimeframeFollowUp?: boolean;
  // Custom valuation component
  enableCustomValuationComponent?: boolean;
  customValuationMessage?: string;
  customValuationButton?: string;
  customValuationTimer?: number; // Timer in milliseconds
  // Agent contact notice control
  agentContactNoticeNull?: boolean; // If true, hides the agent contact notice text
  // Valuation confirmation (blurred value)
  enableValuationConfirmation?: boolean;
  isDismissed?: boolean; // Add optional flag for minimized state
  hideWidgetWhenDismissed?: boolean; // Add optional flag for hiding widget when dismissed
  parentUrl?: string;
  showCircleOnUrls?: string[];
  askEmail?: boolean; // Add optional flag for asking email
  addRentingFlow?: boolean;
  askAsbestosAndFinishLevel?: boolean;
  noValuation?: boolean; // Skip valuation display and show contact message instead
  abTestVersion?: string;
  sessionId?: string; // Add session ID for impression tracking
  inline?: boolean; // Add flag for inline embedded mode
}

interface ChatProps {
  defaultLocale: string;
}

// Define the structure for comparable properties
interface ComparableProperty {
  id: string;
  lat: number;
  lng: number;
  price: number;
  address: string;
  livingArea: number;
  bedrooms: number;
  energyClass: string;
  plotSurface: number;
  buildingConditionScore: number;
  numberOfFrontages: number;
}

// Define ContactInfo type used in Step 6
interface ContactInfo {
  name?: string; // Add optional name
  phone: string;
  email?: string;
}

// Create a logger instance for AVM agent
const logger = new AgentLogger('avm-agent');

// Enhanced error handler for AvmAgent with comprehensive logging
const handleAvmError = (error: any, context: string, additionalData?: any) => {
  // Log to avm-agent.log with full context
  logger.error(`CRITICAL: AvmAgent error in ${context} - potential privacy/security issue`, {
    failure_type: 'avm_agent_error',
    reason: 'Unexpected error in AVM agent component',
    error_message: error instanceof Error ? error.message : 'Unknown error',
    error_stack: error instanceof Error ? error.stack : undefined,
    context,
    additional_data: additionalData,
    impact: 'AVM functionality impaired, user experience affected',
    // Include session context without PII
    has_chat_id: !!additionalData?.chatId,
    has_guest_id: !!additionalData?.guestId,
    has_agency_id: !!additionalData?.agencyId,
    timestamp: new Date().toISOString()
  });

  // Send to Slack for immediate alert
  sendErrorToSlack({
    error: {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      type: 'AvmAgentError'
    },
    context: {
      endpoint: `/app/[locale]/avm/_components/AvmAgent - ${context}`,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'development'
    },
    userData: {
      guestId: additionalData?.guestId,
      chatId: additionalData?.chatId,
      agencyId: additionalData?.agencyId
    },
    requestPayload: {
      context,
      additionalData,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  });
};

// Impression tracking function
const trackImpression = async (
  sessionId: string | undefined,
  urlConfig: ChatConfig | null,
  chatId: string | undefined,
  guestId: string | undefined,
  leadSourceDataFromWidget: any,
  messages: Message[],
  impressionType: 'open' | 'close' | 'minimize',
  lastWorkflowStep?: string
) => {
  if (!sessionId || !urlConfig?.agencyId) {
    console.warn('Cannot track impression: missing sessionId or agencyId');
    return;
  }

  try {
    const fullTrackingData = captureLeadSourceData(leadSourceDataFromWidget);
    
    // Only send the essential tracking data
    const trackingData = {
      deviceType: fullTrackingData.deviceType,
      browser: fullTrackingData.browser,
      parentUrl: fullTrackingData.sourceUrl,
      abTestVersion: fullTrackingData.abTestVersion
    };
    
    const response = await fetch('/api/impressions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        agencyId: urlConfig.agencyId,
        agentId: urlConfig.agentId,
        chatId,
        guestId,
        impressionType,
        trackingData,
        lastWorkflowStep
      })
    });

    if (!response.ok) {
      console.error('Failed to track impression:', await response.text());
    }
  } catch (error) {
    console.error('Error tracking impression:', error);
  }
};

function AvmAgent({ defaultLocale }: ChatProps) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // --- Add ref for the last message ---
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dictionary, setDictionary] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const [chatId, setChatId] = useState<string | undefined>(undefined);
  const [guestId, setGuestId] = useState<string | undefined>(undefined);
  const [detectedLanguage, setDetectedLanguage] = useState<string>('nl'); // Default to English
  const [urlConfig, setUrlConfig] = useState<ChatConfig | null>(null);
  const [locale, setLocale] = useState<string>(defaultLocale || 'nl');
  const [agentId, setAgentId] = useState<string | null>(null);
  const [abTestVersion, setAbTestVersion] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [sessionStarted, setSessionStarted] = useState<boolean>(false);
  const [chatBackgroundColor, setChatBackgroundColor] = useState<string>('#ffffff'); // State for background color, default to white
  const [chatTextStyle, setChatTextStyle] = useState<React.CSSProperties>({});
  const [isReady, setIsReady] = useState(false); // State to track initial loading
  const [chatFaviconUrl, setChatFaviconUrl] = useState<string | null>(null); // State for favicon URL, default null
  const [avmFetchPromise, setAvmFetchPromise] = useState<Promise<any> | null>(null); // State to hold the background AVM fetch promise
  // --- Add state for phone verification persistence ---
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  // --- Add state for tracking flow completion ---
  const [isFlowCompleted, setIsFlowCompleted] = useState(false);
  // --- Add state for minimized circular button ---
  const [isMinimized, setIsMinimized] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // --- Add state for quit reason modal (visibility + saved result) ---
  const [showQuitReasonModal, setShowQuitReasonModal] = useState(false);
  const [chatQuitReason, setChatQuitReason] = useState<string | null>(null);
  // --- Add comprehensive loading state ---
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  // --- Add basic config loaded state for immediate display ---
  const [isBasicConfigLoaded, setIsBasicConfigLoaded] = useState(false);
  // --- Add state to store lead source data from widget loader ---
  const [leadSourceDataFromWidget, setLeadSourceDataFromWidget] = useState<any>(null);
  // --- Add state to hold the parent URL safely ---
  const [parentUrl, setParentUrl] = useState<string | null>(null);
  const [isCloseButtonHovered, setIsCloseButtonHovered] = useState(false);
  // --- Add state to prevent initial render before first URL is processed ---
  // const [initialUrlProcessed, setInitialUrlProcessed] = useState(false);

  // --- Desktop flag determined by parent (loader) to avoid iframe misclassification ---
  // Initialize with window width detection for immediate correct styling
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > 768;
    }
    return false;
  });
  // --- Stacked teaser reveal control (desktop only) ---
  const [showClosedTeaser, setShowClosedTeaser] = useState(true);
  // Track if we've already performed the delayed stacked reveal once this session
  const stackedRevealDoneRef = useRef(false);

  // --- Session resume: no parent persistence; keep state local to component ---

  // --- Refs for timers and flags are no longer needed with server-side orchestration ---

  const [sellingState, setSellingState] = useState<{
    contactInfo?: ContactInfo; // Updated type
    privacyConsent?: boolean;
    propertyInfo?: {
      epcValue: string;
      condition: string;
      asbestos?: string;
      finishLevel?: string;
    };
    propertyDetails?: {
      livingArea: number;
      bedrooms: number;
      bathrooms: number;
      plotArea?: number;
      facade?: number;
      transactionCount?: number;
    };
    streetName?: string;
    houseNumber?: string;
    zipCode?: string;
    city?: string;
    propertyType?: 'apartment' | 'house';
    isInterestedInValuation?: boolean;
    additionalDetails?: any;
    priceRange?: { min: number; max: number };
    avmResult?: any;
    comparables?: ComparableProperty[];
    subject_latitude?: number;
    subject_longitude?: number;
    // cache for fast nearby preview
    nearbyComparables?: Array<{ id: string; lat: number; lng: number; address?: string }>;
    nearbyPreFetchPromise?: Promise<Array<{ id: string; lat: number; lng: number; address?: string }>>;
    latitude?: number;
    longitude?: number;
    // --- Add star rating to state --- 
    starRating?: number;
    // --- Add flag for details response (optional, added in previous step but ensure it's here) ---
    wantsDetails?: boolean;
    // --- Add selling timeframe ---
    sellingTimeframe?: 'now' | '6m' | '12m' | 'curious' | 'no';
    // Inline follow-up move plan (step 6)
    movePlan?: 'sell_then_buy' | 'bought_then_sell' | 'sell_only' | 'buy_then_sell';
    // --- Add lead source data for tracking ---
    leadSourceData?: {
      deviceType: 'mobile' | 'desktop';
      browser: string;
      sourceUrl: string;
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
      utmTerm?: string;
      utmContent?: string;
      gclid?: string;
      fbclid?: string;
    };
    // --- Add rent form submission state ---
    rentFormSubmitted?: boolean;
    rentFormOwnershipStatus?: boolean; // true = owner, false = tenant
    // --- Add valuation confirmation choice ---
    valuationConfirmed?: boolean | null; // true = wants contact, false = doesn't, null = not answered
  }>({});

  useEffect(() => {
    // Let the parent know the widget is ready to receive initial data
    window.parent.postMessage({ type: 'AVM_WIDGET_READY' }, '*');
  }, []);

  const sellingStateRef = useRef(sellingState);
  useEffect(() => {
    sellingStateRef.current = sellingState;
  }, [sellingState]);

  // Update locale when defaultLocale prop changes
  useEffect(() => {
    if (defaultLocale) {
      setLocale(defaultLocale);
    }
  }, [defaultLocale]);

  // Function to get config from URL parameters
  const getConfigFromUrl = (): ChatConfig => {
    if (typeof window === 'undefined') return {};

    try {
    // Try URL parameters
      const params = new URLSearchParams(window.location.search);
      const encodedParentUrl = params.get('config');
      if (encodedParentUrl) {
        const config = JSON.parse(encodedParentUrl);
        return config;
      }
    } catch (error) {
      console.error('Error parsing config from URL:', error);
    }
    return {};
  };

  // Initialize minimized state from config
  /* This is being removed and merged into the main initialize effect to prevent a race condition
  useEffect(() => {
    const config = getConfigFromUrl();
    if (config.isDismissed) {
      setIsMinimized(true);
    }
  }, []);
  */
  
  // Effect for one-time initialization of config and agency data
  useEffect(() => {
    const initialize = async () => {
      let initialConfig: any = {};
      
      try {
        const config = getConfigFromUrl();
        const agencyConfig = getAgencyConfig(config.agencyId, { 
          backgroundColor: config.backgroundColor, 
          textStyle: config.textStyle 
        });
        initialConfig = { ...config, ...agencyConfig };

        // --- ADD DEACTIVATION CHECK ---
        if (initialConfig.isDeactivated) {
          // Send a message to the parent loader script to remove the iframe
          window.parent.postMessage({ type: 'SELF_DESTRUCT' }, '*');
          // Abort the rest of the initialization and rendering
          return;
        }
        setParentUrl(initialConfig.parentUrl);
        setSessionId(initialConfig.sessionId);

        // Handle dismissed state from parent on initialization
        if (initialConfig.isDismissed) {
          const parentUrl = initialConfig.parentUrl;
          const onSpecialUrl = initialConfig.showCircleOnUrls?.some((url: string) => parentUrl?.startsWith(url));

          let showCircle;
          if (onSpecialUrl) {
            showCircle = true;
          } else {
            showCircle = !initialConfig.hideWidgetWhenDismissed;
          }

          if (showCircle) {
            // Otherwise, show the circular button.
            setIsMinimized(true);
          } else {
            // If configured to hide completely, abort rendering by never setting isConfigLoaded.
            window.parent.postMessage({ type: 'SELF_DESTRUCT' }, '*');
            return;
          }
        }

        // --- SET BASIC CONFIG IMMEDIATELY FOR FAST DISPLAY ---
        setUrlConfig(initialConfig);
        setIsChatOpen(initialConfig?.startOpen ?? false);
        // Ensure urlConfig is set before marking as loaded
        setIsBasicConfigLoaded(true); // Show widget immediately with basic config

        // Set initial message right away
        setMessages([{
          role: 'assistant',
          content: '|SHOW_SELLING_FORM',
          classification: {
            primary_intent: 'initiate_sales_avm',
            workflowStep: 'sales-avm-step1-intro'
          },
          workflowStep: 'avm_form'
        }]);

        // --- LOAD AGENCY DATA IN BACKGROUND (NON-BLOCKING) ---
        if (initialConfig.agencyId) {
          try {
            const agencyResponse = await fetch(`/api/agency?agencyId=${initialConfig.agencyId}`);
            if (agencyResponse.ok) {
              const agencyData = await agencyResponse.json();
              // Merge fetched data, preserving any initial URL params over fetched data if they conflict
              const enhancedConfig = { ...agencyData, ...initialConfig };
              setUrlConfig(enhancedConfig); // Update config with agency data
            }
          } catch (error) {
            console.error('Error fetching agency data:', error);
          }
        }

        // Mark full config as loaded
        setIsConfigLoaded(true);

      } catch (error) {
        handleAvmError(error, 'Initialization', { agencyId: initialConfig?.agencyId });
        // Set a minimal config to prevent rendering issues
        setUrlConfig(initialConfig || {});
        // Still mark as loaded even if there's an error to prevent infinite loading
        setIsConfigLoaded(true);
        setIsBasicConfigLoaded(true); // Ensure widget shows even on error
      }
    };

    initialize();
  }, []); // Run only once on mount

  // Effect for fetching data that depends on locale or when config is ready
  useEffect(() => {
    // Wait for config AND the first URL message from the parent before doing anything
    if (!urlConfig) return;

    const fetchDependentData = async () => {
      if (!urlConfig?.agentId) {
        console.error('Agent ID missing');
        // Potentially set an error state for the user
        return;
      }

      // Set state based on the now-stable urlConfig
      setAgentId(urlConfig.agentId);
      setChatBackgroundColor(urlConfig?.backgroundColor ?? '#ffffff');
      setChatFaviconUrl(urlConfig?.faviconUrl ?? null);
      setChatTextStyle(urlConfig?.textStyle ?? {});
      setAbTestVersion(urlConfig?.abTestVersion ?? null);
      
      // Important: Use the locale from state, but default to config if locale hasn't been changed by parent
      const currentLocale = locale || urlConfig?.defaultLocale || 'nl';
      if (locale !== currentLocale) {
        setLocale(currentLocale);
      }

      // --- LOAD DICTIONARY IN BACKGROUND (NON-BLOCKING) ---
      const questions = null;
      // if (urlConfig.agencyId) {
      //   try {
      //     const response = await fetch(`/api/questions/${urlConfig.agencyId}?locale=${currentLocale}`);
      //     if (response.ok) {
      //       questions = await response.json();
      //     }
      //   } catch (error) {
      //     // Silently handle any errors - questions will remain null and defaults will be used
      //   }
      // }
      
      try {
        const dict = await getDictionary(currentLocale, questions);
        setDictionary(dict);
      } catch (error) {
        console.error('Error loading dictionary:', error);
        // Widget will use fallback text from dictionary defaults
      }
      setIsReady(true); // Now we are ready to show

      // No external session snapshot – keep state local only
    };

    fetchDependentData();
  }, [urlConfig, locale]);

  useEffect(() => {
    if (!isLoading) {
      if (inputRef.current) {
        inputRef.current?.focus(); // Focus after a slight delay
      }
    }
  }, [isLoading]);

  // Always scroll to bottom on new messages
  // useEffect(() => {
  //   // --- Use scrollIntoView on the last message element --- 
  //   if (lastMessageRef.current) {
  //     // Simple scrollIntoView call
  //     lastMessageRef.current.scrollIntoView(); 
  //   }
  // }, [messages]); // Dependency array includes messages
  // ---- REMOVED this hook as MutationObserver handles it ----

  // --- Auto-scroll effect using MutationObserver ---
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Function to scroll smoothly to the bottom
    const scrollToBottom = () => {
      // Use requestAnimationFrame to wait for next paint cycle
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });
    };

    // Initial scroll to bottom when the effect runs
    scrollToBottom();

    // Create an observer instance linked to a callback function
    const observer = new MutationObserver((mutationsList) => {
      // --- Check if ANY mutation in this batch originated from the map ---
      let shouldScroll = true; // Assume we should scroll by default
      for (const mutation of mutationsList) {
        // Check if the mutation target or its parent is the map area
        if (mutation.target instanceof Node && mutation.target.nodeType === Node.ELEMENT_NODE) {
          // Check the target element itself
          if ((mutation.target as HTMLElement).closest('.google-map-interaction-area')) {
            shouldScroll = false;
            break; // Found a map mutation, no need to check others in this batch
          }
        }
        // Also check added nodes, as the target might be the messages container
        // but a map node was added
        for (const addedNode of mutation.addedNodes) {
          if (addedNode instanceof HTMLElement && addedNode.closest('.google-map-interaction-area')) {
            shouldScroll = false;
            break; // Found a map mutation
          }
        }
        // If we've decided not to scroll, exit the outer loop too
        if (!shouldScroll) break; 
      }

      // --- Only scroll if no map-related mutations were detected ---
      if (shouldScroll) {
        scrollToBottom();
      }
    });

    // Start observing the target node for configured mutations
    // --- Observe attributes and characterData as well ---
    const config = { childList: true, subtree: true, attributes: true, characterData: true };
    observer.observe(container, config);

    // Cleanup function to stop observing when the component unmounts
    return () => {
      observer.disconnect();
    };
  }, [messages]); // Re-run when messages array changes to ensure observer is attached
  // --- End Auto-scroll effect ---

  // Save message to DB
  const saveMessage = useCallback(async (message: Message) => {
    if (!message.chatId || !message.userId) {
      console.error('Cannot save message:', message);
      return;
    }

    try {
      await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...message,
          createdAt: new Date(),
        }),
      });
    } catch (error) {
      console.error('Error saving message:', error);
    }
  }, []);

  const formatMessage = useCallback((content: string) => {
    // Handle bold text
    content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Clean up multiple newlines first
    content = content.replace(/\n{3,}/g, '\n\n'); // Replace 3+ newlines with 2
    
    // Preserve double line breaks and handle bullet points
    return content
      .split(/\n\n/) // Split on double line breaks first
      .map(paragraph => {
        if (paragraph.trim() === '') return '<div class="h-4"></div>'; // Increased spacing
        const lines = paragraph.split('\n').map(line => {
          line = line.trim(); // Trim each line
          if (line.startsWith('• ')) return `<div class="pl-2">${line}</div>`;
          if (line.startsWith('○ ')) return `<div class="pl-4">${line}</div>`;
          if (line.startsWith('- ')) return `<div class="pl-8">${line.replace(/^- /, '- ')}</div>`; // Add more indent for sub-sub-points
          return `<div>${line}</div>`;
        });
        return lines.join('');
      })
      .join('<div class="h-4"></div>'); // Add spacing between paragraphs
  }, []);

  // Handle widget open with session tracking
  const handleWidgetOpen = useCallback(() => {
    if (!sessionStarted) {
      // First time opening - start session tracking
      if (sessionId && urlConfig) {
        trackImpression(
          sessionId,
          urlConfig,
          chatId,
          guestId,
          leadSourceDataFromWidget,
          messages,
          'open'
        );
      }
      setSessionStarted(true);
    } else {
      // Subsequent opens - update existing session
      if (sessionId && urlConfig) {
        trackImpression(
          sessionId,
          urlConfig,
          chatId,
          guestId,
          leadSourceDataFromWidget,
          messages,
          'open'
        );
      }
    }
    setIsChatOpen(true);
  }, [sessionStarted, sessionId, urlConfig, chatId, guestId, leadSourceDataFromWidget, messages]);

  // Update session with workflow step without counting as impression
  const updateSessionWorkflowStep = useCallback(async (
    sessionId: string,
    urlConfig: ChatConfig,
    lastWorkflowStep: string
  ) => {
    if (!sessionId || !urlConfig?.agencyId) {
      console.warn('Cannot update workflow step: missing sessionId or agencyId');
      return;
    }

    try {
      const response = await fetch('/api/impressions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          agencyId: urlConfig.agencyId,
          agentId: urlConfig.agentId,
          chatId,
          guestId,
          lastWorkflowStep,
          updateWorkflowOnly: true // Flag to indicate this is workflow update only
        })
      });

      if (!response.ok) {
        console.error('Failed to update workflow step:', await response.text());
      }
    } catch (error) {
      console.error('Error updating workflow step:', error);
    }
  }, [chatId, guestId]);

  const reportAddressError = useCallback((error: { code: string; message: string; details?: Record<string, any> }) => {
    const errorId = `address-resolution-${Date.now()}`;
    logger.error('Address resolution failure', {
      error_id: errorId,
      failure_type: error.code,
      message: error.message,
      details: error.details,
      chat_id: chatId,
      guest_id: guestId,
      agency_id: urlConfig?.agencyId
    });
    sendErrorToSlack({
      error: {
        message: `[Address Resolution Failure] ${error.message}`
      },
      context: {
        endpoint: '/app/[locale]/avm/_components/AddressForm',
        timestamp: new Date().toISOString(),
        errorId: errorId
      },
      userData: {
        chatId,
        guestId,
        agencyId: urlConfig?.agencyId
      },
      requestPayload: {
        errorId,
        reason: error.code,
        details: error.details
      }
    });
  }, [chatId, guestId, urlConfig?.agencyId]);

  const minimizeChat = useCallback(() => {
    // After closing the chat, keep only the circle visible for the rest of the session
    setIsChatOpen(false);
    setIsMinimized(true);
    setShowClosedTeaser(false);
    try { stackedRevealDoneRef.current = true; } catch (_) {}
    
    // Track minimize impression (only if session has started)
    if (sessionStarted && sessionId && urlConfig) {
      trackImpression(
        sessionId,
        urlConfig,
        chatId,
        guestId,
        leadSourceDataFromWidget,
        messages,
        'minimize'
      );
    }
  }, [sessionStarted, sessionId, urlConfig, chatId, guestId, leadSourceDataFromWidget, messages]);

  // Helper function to check if quit reason modal should be shown
  const shouldShowQuitReasonModal = useCallback(() => {
    
    // Check if widget is open
    if (!isChatOpen) {
      return false;
    }

    // If the user has already verified phone or completed the flow, don't ask why they quit
    if (isPhoneVerified || isFlowCompleted) {
      return false;
    }
        
    // Calculate current step number based on workflow step
    const lastFormMessage = messages
      .slice()
      .reverse()
      .find(m => m.role === 'assistant' && m.content?.includes('|SHOW_SELLING_FORM') && m.classification?.workflowStep);
    
    const currentWorkflowStep = lastFormMessage?.classification?.workflowStep;
    let currentStepNumber = 0;
    
    if (currentWorkflowStep === 'sales-avm-rent-contact') {
      currentStepNumber = 2;
    } else if (currentWorkflowStep) {
      const stepIndex = AVM_STEPS.indexOf(currentWorkflowStep);
      
      if (stepIndex !== -1) {
        currentStepNumber = stepIndex + 1; // Start with 1-based index
        if (currentStepNumber > 8) {
          currentStepNumber = 8; // Cap at 8
        }
      } else {
      }
    } else {
    }
        
    if (chatQuitReason) {
      return false;
    }

    // If we've reached the final evaluation step, don't show the modal
    if (currentWorkflowStep === 'sales-avm-step9-final-evaluation' || currentWorkflowStep === 'completed') {
      return false;
    }
    
    // Check if current step is 3, 4, 5, 6, 7, or 8
    const isInTargetRange = currentStepNumber >= 3 && currentStepNumber <= 8;
    
    if (!isInTargetRange) {
      return false;
    }
    
    return true;
  }, [isChatOpen, messages, chatQuitReason, isPhoneVerified, isFlowCompleted]);


  // Helper function to handle the deterministic form transition
  const handleDeterministicStep = async (currentStep: string, userMessageContent: string, formData?: any) => {
    try {
      setIsLoading(true);

      const newUserMessage: Message = {
        role: 'user',
        content: userMessageContent,
        chatId: chatId,
        userId: guestId,
        classification: { primary_intent: 'provide_data', workflowStep: currentStep },
        workflowStep: 'avm_form'
      };

      if (!hasInitialized) {
        logger.error('CRITICAL: Chat not initialized when trying to submit form data - blocking user action', {
          failure_type: 'session_not_initialized',
          reason: 'User attempted action before chat session was fully initialized',
          current_workflow_step: currentStep,
          chat_id: chatId,
          guest_id: guestId,
          agent_id: urlConfig?.agencyId,
          has_chat_id: !!chatId,
          has_guest_id: !!guestId,
          impact: 'User cannot proceed with AVM, session state incomplete'
        });
        setError('Chat session error. Please refresh.');
        setIsLoading(false);
        return;
      }
      // Show the user's message immediately for snappier UX
      setMessages(prev => [...prev, newUserMessage]);
      // Fire-and-forget save to avoid blocking UI on network
      try {
        if (newUserMessage.chatId && newUserMessage.userId) {
          // Do not await — persist in background
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          saveMessage(newUserMessage);
        }
      } catch (_) {}

      // Start typing indicator right away while we compute the next step
      const startedAt = Date.now();
      const typingId = showAssistantTyping();

      let nextStep: string | null = null;
      let avmResultData: any = null; // To store AVM result if called

      // Special handling for end of step 6 (Contact Info)
      if (currentStep === 'sales-avm-step6-contact') {
        try {
        // Construct payload for /api/avm
          const avmPayload = {
          // Ensure all necessary data from sellingState is included
          // Use defaults or handle missing data gracefully
            'Postal Code': sellingState.zipCode || '',
            'Living Area': Number(sellingState.propertyDetails?.livingArea) || 0,
            'Building Condition Score': sellingState.propertyInfo?.condition ? parseInt(sellingState.propertyInfo.condition) : 3,
            'Property Type': sellingState.propertyType || 'house',
            'Address': `${sellingState.streetName} ${sellingState.houseNumber}, ${sellingState.zipCode} ${sellingState.city}`,
            'Plot Surface': sellingState.propertyDetails?.plotArea || 0,
            'New Construction': false, // Assuming false unless captured
            'Energy Class': sellingState.propertyInfo?.epcValue || 'unknown',
            'Asbestos': sellingState.propertyInfo?.asbestos,
            'Finish Level': sellingState.propertyInfo?.finishLevel,
            'Number Of Frontages': sellingState.propertyDetails?.facade || (sellingState.propertyType === 'apartment' ? 2 : 4),
            'Bedrooms': sellingState.propertyDetails?.bedrooms || 0,
            'Latitude': sellingState.latitude,
            'Longitude': sellingState.longitude,
          // Add any other required fields for your /api/avm endpoint
          };

          // --- Add a check/log for missing coordinates --- START
          if (sellingState.latitude == null || sellingState.longitude == null) {
            logger.error('CRITICAL: Latitude or Longitude missing from sellingState before AVM call - preventing OpenAI fallback', {
              reason: 'Coordinates not available, likely due to session loss or geocoding failure',
              failure_type: 'coordinate_validation_failure',
              impact: 'User cannot proceed with AVM valuation, must restart address entry',
              latitude: sellingState.latitude,
              longitude: sellingState.longitude,
              has_address_data: {
                streetName: !!sellingState.streetName,
                houseNumber: !!sellingState.houseNumber,
                zipCode: !!sellingState.zipCode,
                city: !!sellingState.city
              },
              current_workflow_step: currentStep,
              chat_id: chatId,
              guest_id: guestId,
              agent_id: urlConfig?.agencyId,
              session_initialized: hasInitialized,
              // Log minimal state for debugging without exposing PII
              property_type: sellingState.propertyType,
              has_property_details: !!sellingState.propertyDetails,
              has_contact_info: !!sellingState.contactInfo
            });
            sendErrorToSlack({
              error: {
                message: 'Latitude or Longitude missing before AVM call'
              },
              context: {
                endpoint: '/app/[locale]/avm/_components/AvmAgent - Step6 AVM call',
                timestamp: new Date().toISOString(),
                environment: process.env.NODE_ENV || 'development'
              },
              userData: {
                guestId,
                chatId,
                agencyId: urlConfig?.agencyId
              },
              requestPayload: {
                latitude: sellingState.latitude,
                longitude: sellingState.longitude,
                streetName: sellingState.streetName,
                houseNumber: sellingState.houseNumber,
                zipCode: sellingState.zipCode,
                city: sellingState.city
              }
            });
            logger.info('User notified: coordinate validation failure - sent error message to user');
            setError('Location coordinates are missing. Please go back to the address step and try again.');
            setIsLoading(false);
            return;
          }
          // --- Add a check/log for missing coordinates --- END

          const avmResponse = await fetch('/api/avm', { // Assuming this is your AVM endpoint
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messages: [{ role: 'user', content: JSON.stringify(avmPayload) }] }) // Adjust payload structure if needed
          });

          avmResultData = await avmResponse.json(); // Store the full result
          if (!avmResponse.ok) {
            throw new Error(`AVM API returned ${JSON.stringify(avmResultData)}`);
          }

          // Extract price range (adjust based on actual API response structure)
          const priceRange = avmResultData?.priceRange || avmResultData?.valuation?.priceRange || { min: 0, max: 0 };
          setSellingState(prev => ({ ...prev, priceRange, avmResult: avmResultData }));

          // Now call workflow to get the next step (should be step 7)
          const workflowResponse = await fetch('/api/avm/workflow', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ currentStep }),
          });

          if (!workflowResponse.ok) {
            throw new Error(`Workflow API returned ${workflowResponse.status} after AVM call`);
          }
          const workflowData = await workflowResponse.json();
          nextStep = workflowData.nextStep; // Should be 'sales-avm-step7-valuation'

        } catch (avmError) {
          // Check if this is a postal code data issue
          const isPostalCodeError = avmError instanceof Error &&
            (avmError.message.includes('Area Classification not found') ||
             avmError.message.includes('Postal Code'));

          logger.error('CRITICAL: AVM API call failed after step 6 - potential OpenAI fallback risk', {
            failure_type: isPostalCodeError ? 'postal_code_data_error' : 'avm_api_failure',
            reason: isPostalCodeError ?
              'Postal code not found in database - may need data update' :
              'AVM calculation failed, may fall back to OpenAI if coordinates missing',
            error_message: avmError instanceof Error ? avmError.message : 'Unknown AVM error',
            current_workflow_step: currentStep,
            chat_id: chatId,
            guest_id: guestId,
            agent_id: urlConfig?.agencyId,
            has_coordinates: {
              latitude: sellingState.latitude != null,
              longitude: sellingState.longitude != null
            },
            property_type: sellingState.propertyType,
            postal_code: sellingState.zipCode,
            impact: isPostalCodeError ?
              'User postal code not in database - may need fallback or data fix' :
              'User cannot get valuation, workflow blocked'
          });
          setError('Failed to get property valuation. Please try again or contact support.');
          setIsLoading(false);
          return;
        }
      } else {
      // For all other steps, just call the workflow endpoint
        try {
          const workflowResponse = await fetch('/api/avm/workflow', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ currentStep }),
          });

          if (!workflowResponse.ok) {
            throw new Error(`Workflow API returned ${workflowResponse.status}`);
          }
          const workflowData = await workflowResponse.json();
          nextStep = workflowData.nextStep;
        } catch (error) {
          logger.error('CRITICAL: Workflow API call failed - cannot determine next step', {
            failure_type: 'workflow_api_failure',
            reason: 'Cannot progress through AVM workflow',
            error_message: error instanceof Error ? error.message : 'Unknown workflow error',
            current_workflow_step: currentStep,
            chat_id: chatId,
            guest_id: guestId,
            agent_id: urlConfig?.agencyId,
            impact: 'User workflow blocked, cannot continue AVM process'
          });
          setError('Failed to determine next step. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      // Handle the next step
      if (nextStep && nextStep === 'sales-avm-step9-final-evaluation') {
      // --- If workflow says next is Step 9, trigger final message ---
        // Format the final message using the priceRange from the current state
        const finalMessageContent = (dictionary?.sellingForm?.finalThankYou || 
          'Thank you! Your valuation estimate is between €[MIN_PRICE] and €[MAX_PRICE]. We will be in touch.')
          .replace('[MIN_PRICE]', sellingState.priceRange?.min?.toLocaleString(['nl-BE']) ?? 'N/A')
          .replace('[MAX_PRICE]', sellingState.priceRange?.max?.toLocaleString(['nl-BE']) ?? 'N/A');
          
        const finalAssistantMessage: Message = {
          role: 'assistant',
          content: finalMessageContent,
          chatId: chatId,
          userId: agentId as string,
          classification: { primary_intent: 'sales_avm_complete', workflowStep: 'completed' }
        };

        await ensureMinDelay(startedAt);
        replaceTypingWith(typingId, finalAssistantMessage);
        await saveMessage(finalAssistantMessage);
        setIsLoading(false);
        logger.info('[AVM step-update] Moving to final step9');
        await updateChatCurrentStep('sales-avm-step9-final-evaluation');

      } else if (nextStep) { // If workflow provides a different next step (next form to show)
      // --- Standard next step logic ---
        const nextAssistantMessage: Message = {
          role: 'assistant',
          content: '|SHOW_SELLING_FORM', 
          chatId: chatId,
          userId: agentId as string,
          classification: { primary_intent: 'sales_avm', workflowStep: nextStep },
          workflowStep: 'avm_form'
        // Optionally add AVM result to message if needed for display
        // properties: nextStep === 'sales-avm-step7-valuation' ? avmResultData : undefined
        };

        await ensureMinDelay(startedAt);
        replaceTypingWith(typingId, nextAssistantMessage);
        await saveMessage(nextAssistantMessage);
        setIsLoading(false);
        logger.info('[AVM step-update] Moving to next step', { nextStep });
        await updateChatCurrentStep(nextStep);

      } else if (nextStep === null && currentStep === 'sales-avm-step9-final-evaluation') {      // --- This block is now effectively handled by the logic above --- 
      // Keep it potentially as a fallback or remove if confident.
      // For now, let's log if we ever reach here unexpectedly.
        logger.warn('Reached final evaluation processing block unexpectedly.');
        // ... existing final message logic ... (can likely be removed)
        setIsLoading(false);
      } else {
      // Unexpected response from workflow API or end condition met prematurely
        logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} from ${currentStep}.`);
        hideAssistantTyping(typingId);
        setError('Could not determine the next step automatically.');
        setIsLoading(false);
      }
    } catch (error) {
      handleAvmError(error, 'handleDeterministicStep', { 
        currentStep, 
        chatId, 
        guestId, 
        agencyId: urlConfig?.agencyId 
      });
      setError('An error occurred while processing your request. Please try again.');
      setIsLoading(false);
    }
  };

  const renderSellingForm = (message: Message) => {
    const currentMessage = messages[messages.length - 1];
    const isCurrentMessage = message === currentMessage;


    if (!message.content?.includes('|SHOW_SELLING_FORM')) {
      return null;
    }

    // Ensure dictionary is loaded before rendering any specific form
    if (!dictionary) {
      logger.warn('Dictionary not loaded, cannot render form');
      return <div className="text-sm text-gray-500">Loading form...</div>; // Or some placeholder
    }

    switch (message.classification?.workflowStep) {
      case 'sales-avm-step1-intro':

        return (
          <IntroForm
            dictionary={dictionary}
            agencyId={urlConfig?.agencyId}
            addRentingFlow={urlConfig?.addRentingFlow}
            onSubmit={async (choice) => {

              if (choice === 'rent') {
                const rentResponseMessage = dictionary?.sellingForm?.rentEstimateMessage || 'Ik wil een huurprijs schatten';
                const newUserMessage: Message = {
                  role: 'user' as const,
                  content: rentResponseMessage,
                  chatId: chatId,
                  userId: guestId,
                  classification: { primary_intent: 'selection', workflowStep: 'sales-avm-step1-intro' },
                  workflowStep: 'avm_form'
                };
                setMessages(prev => [...prev, newUserMessage]);
                setIsLoading(true);

                const nextAssistantMessage: Message = {
                  role: 'assistant',
                  content: '|SHOW_SELLING_FORM',
                  chatId: chatId,
                  userId: agentId as string,
                  classification: { primary_intent: 'sales_avm', workflowStep: 'sales-avm-rent-contact' },
                  workflowStep: 'avm_form'
                };

                const startedAt = Date.now();
                const typingId = showAssistantTyping();
                await ensureMinDelay(startedAt);
                replaceTypingWith(typingId, nextAssistantMessage);
                setIsLoading(false);

                // Persist step: rent contact
                logger.info('[AVM step-update] Intro rent path -> rent-contact');
                updateChatCurrentStep('sales-avm-rent-contact');

                return;
              }

              const currentStep = 'sales-avm-step1-intro';
              
              const readyResponseText = urlConfig?.addRentingFlow
                ? dictionary?.sellingForm?.propertyEstimateMessage || 'Ik wil een woning schatting'
                : dictionary?.sellingForm?.readyResponse || 'Yes';

              // Create the user message object first
              const newUserMessage: Message = {
                role: 'user' as const,
                content: readyResponseText,
                chatId: chatId, // Use state chatId (might be undefined initially)
                userId: guestId, // Use state guestId (might be undefined initially)
                classification: { primary_intent: 'affirm', workflowStep: currentStep },
                workflowStep: 'avm_form'
              };

              // --- Capture lead source data here (moved from step 2) ---
              if (!sellingState.leadSourceData) {
                const leadSourceData = captureLeadSourceData(leadSourceDataFromWidget);
                setSellingState(prev => ({ 
                  ...prev, 
                  leadSourceData 
                }));
              }

              // --- Optimized Immediate UI Update (User Message Only) --- 
              setMessages(prev => [...prev, newUserMessage]);
              setIsLoading(true); // Start loading indicator immediately
              
              // --- Delayed UI Update (Next Form) & Background Processing --- 
              
              // Define the background task function
              const initializeAndSave = async () => {
                let definitiveChatId = chatId;
                let definitiveGuestId = guestId;
                let justInitialized = false;
                try {
                  // 1. Initialize if needed (can run concurrently with timeout)
                  if (!hasInitialized) {
                    if (!agentId) throw new Error('Agent ID missing during chat initialization');
                    
                    const initResponse = await fetch('/api/conversations', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ 
                        agencyId: urlConfig?.agencyId, 
                        agentId, 
                        type: 'CHAT',
                        abTestVersion: abTestVersion
                      } as Chat),
                    });
                    if (!initResponse.ok) {
                      throw new Error(`Failed to init chat via API: ${initResponse.statusText}`);
                    }
                    
                    const chatData = await initResponse.json();
                    definitiveChatId = chatData.chatId;
                    definitiveGuestId = chatData.guestId;
                    justInitialized = true; 
                    


                    // Update state *after* successful initialization
                    setChatId(definitiveChatId);
                    if (!localStorage.getItem('chatId')) {
                      localStorage.setItem('chatId', definitiveChatId as string);
                    }
                    setGuestId(definitiveGuestId);
                    setHasInitialized(true); 
                  }

                  // 2. Save the user message (now with definitive IDs)
                  if (definitiveChatId && definitiveGuestId) {
                    await saveMessage({...newUserMessage, chatId: definitiveChatId, userId: definitiveGuestId });
                  } else {
                    logger.error('Cannot save user message, missing chatId or guestId after initialization attempt');
                    throw new Error('Chat identification failed.'); // Throw to prevent saving assistant msg
                  }

                  // 3. Prepare the assistant message for the next step
                  const nextAssistantMessage: Message = {
                    role: 'assistant',
                    content: '|SHOW_SELLING_FORM',
                    chatId: definitiveChatId, // Use the definitive chatId
                    userId: agentId as string, 
                    classification: { primary_intent: 'sales_avm', workflowStep: 'sales-avm-step2-address' },
                    workflowStep: 'avm_form'
                  };

                  // 4. Save the assistant message (using definitive IDs)
                  if (definitiveChatId && agentId) {
                    await saveMessage(nextAssistantMessage);
                  } else {
                    logger.error('Cannot save assistant message, missing chatId or agentId');
                    // Potentially throw an error here too
                  }

                  // Persist step: user confirmed intro -> step1
                  logger.info('[AVM step-update] After intro submit, moving to step1-intro (persisted)');
                  await updateChatCurrentStep('sales-avm-step1-intro', definitiveChatId);

                  // Return the prepared assistant message for the timeout callback
                  return nextAssistantMessage;

                } catch (error) {
                  logger.error('Error during intro form background processing:', error);
                  setError(`Failed to initialize or save initial messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  setIsLoading(false); // Ensure loading stops on error
                  return null; // Indicate failure
                }
              };

              // Start the background task immediately (don't await it)
              const backgroundPromise = initializeAndSave();

              const startedAt = Date.now();
              const typingId = showAssistantTyping();
              
              // Show assistant message after 2-second delay, regardless of background task completion
              setTimeout(() => {
                // Create the assistant message immediately (don't wait for background task)
                const nextAssistantMessage: Message = {
                  role: 'assistant',
                  content: '|SHOW_SELLING_FORM',
                  chatId: chatId || 'temp', // Use current chatId or temp
                  userId: agentId as string, 
                  classification: { primary_intent: 'sales_avm', workflowStep: 'sales-avm-step2-address' },
                  workflowStep: 'avm_form'
                };
                
                replaceTypingWith(typingId, nextAssistantMessage);
                setIsLoading(false);
                
                // Handle background task completion separately (fire-and-forget)
                backgroundPromise.then((result) => {
                  if (result) {
                    logger.info('Background task completed successfully');
                  } else {
                    logger.warn('Background task failed, but UI already shown');
                  }
                }).catch((error) => {
                  logger.error('Background task error (non-blocking):', error);
                });
              }, 2000); // Always show after 2 seconds

            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );
      case 'sales-avm-rent-contact':
        return (
          <RentForm
            dictionary={dictionary}
            disableSubmit={!isCurrentMessage || isLoading}
            isSubmitted={sellingState.rentFormSubmitted || false}
            ownershipStatus={sellingState.rentFormOwnershipStatus}
            onSubmit={async (contactInfo) => {
              // Update the state first
              setSellingState(prev => ({
                ...prev,
                rentFormSubmitted: true,
                rentFormOwnershipStatus: contactInfo.isOwner
              }));
              
              // Only call API if user is an owner
              if (contactInfo.isOwner) {
                await fetch('/api/avm/trigger-email-rent', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    locale: locale,
                    agencyId: urlConfig?.agencyId,
                    contactInfo: contactInfo,
                  }),
                });
              }
            }}
          />
        );
      case 'sales-avm-step2-address':

        return (
          <AddressForm
            dictionary={dictionary}
            initialStreetName={sellingState.streetName}
            initialHouseNumber={sellingState.houseNumber}
            initialZipCode={sellingState.zipCode}
            initialCity={sellingState.city}
            initialLatitude={sellingState.latitude}
            initialLongitude={sellingState.longitude}
            initialPropertyType={sellingState.propertyType}
            autoOpen={Boolean(
              sellingState.streetName &&
              sellingState.houseNumber &&
              sellingState.zipCode &&
              sellingState.city &&
              typeof sellingState.latitude === 'number' &&
              typeof sellingState.longitude === 'number'
            )}
            onOpen={() => {
              logger.info('[AVM step-update] Address form opened -> persisting step2-address');
              updateChatCurrentStep('sales-avm-step2-address');
            }}
            onAddressError={reportAddressError}
            onSubmit={({ streetName, houseNumber, zipCode, city, propertyType, latitude, longitude }) => {
              const currentStep = 'sales-avm-step2-address';
              
              const address = `${streetName} ${houseNumber}, ${zipCode} ${city}`;
              
              // Update state immediately (no pre-fetch yet)
              setSellingState(prev => ({ 
                ...prev, 
                streetName,
                houseNumber, 
                zipCode, 
                city, 
                propertyType, 
                latitude,
                longitude
              }));
              
              // Create user message immediately
              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.propertyProvided || 'Here is my property information:'}\n• ${dictionary.sellingForm.propertyAddress || 'Address'}: ${address}\n• ${dictionary.sellingForm.propertyType || 'Property type'}: ${propertyType === 'apartment' ? dictionary.sellingForm.propertyTypeApartment : dictionary.sellingForm.propertyTypeHouse}` :
                `Here is my property information:\n• Address: ${address}\n• Property type: ${propertyType}`;
              handleDeterministicStep(currentStep, formattedMessage, { streetName, houseNumber, zipCode, city, propertyType });
              logger.info('[AVM step-update] After address submit, moving to step3-statbel');
              updateChatCurrentStep('sales-avm-step3-statbel');
              
              // --- NEW: Start location-specific pre-fetch AFTER user message is created ---
              const preFetchNearbyComparables = async () => {
                try {
                  const response = await fetch('/api/avm/nearby', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      lat: latitude,
                      lng: longitude,
                      propertyType: propertyType === 'house' ? 'HOUSE' : 'APARTMENT',
                      radiusKm: 10,
                      limit: 60,
                    }),
                  });
                  
                  if (response.ok) {
                    const data = await response.json();
                    return data.properties || [];
                  }
                  return [];
                } catch (error) {
                  console.warn('Location-specific pre-fetch failed:', error);
                  return [];
                }
              };
              
              // Start pre-fetch in background (fire-and-forget)
              const locationSpecificPreFetch = preFetchNearbyComparables();
              
              // Update state with pre-fetch promise
              setSellingState(prev => ({ 
                ...prev, 
                nearbyPreFetchPromise: locationSpecificPreFetch
              }));
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );
      case 'sales-avm-step3-statbel':
        return (
          <PropertyDetailsForm
            dictionary={dictionary}
            propertyType={sellingState.propertyType || 'apartment'}
            municipality={sellingState.zipCode || ''}
            cityName={sellingState.city || ''}
            subject_latitude={sellingState.latitude}
            subject_longitude={sellingState.longitude}
            initialValues={sellingState.propertyDetails}
            initialNearbyComparables={sellingState.nearbyComparables}
            onNearbyComparables={(comps) => setSellingState(prev => ({ ...prev, nearbyComparables: comps }))}
            nearbyPreFetchPromise={sellingState.nearbyPreFetchPromise}
            onSubmit={async (propertyDetails) => {
              const currentStep = 'sales-avm-step3-statbel';
              setSellingState(prev => ({ ...prev, propertyDetails }));

              // Format the message with property details
              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.propertyDetailsProvided}\n` +
                `• ${dictionary.sellingForm.livingArea}: ${propertyDetails.livingArea} m²\n` +
                `• ${dictionary.sellingForm.bedrooms}: ${propertyDetails.bedrooms}\n` +
                `• ${dictionary.sellingForm.bathrooms}: ${propertyDetails.bathrooms}\n` +
                (propertyDetails.plotArea ? `• ${dictionary.sellingForm.plotArea}: ${propertyDetails.plotArea} m²\n` : '') +
                (propertyDetails.facade ? `• ${dictionary.sellingForm.facade}: ${propertyDetails.facade}\n` : '')
                :
                'Here are my property details:\n' +
                `• Living Area: ${propertyDetails.livingArea} m²\n` +
                `• Bedrooms: ${propertyDetails.bedrooms}\n` +
                `• Bathrooms: ${propertyDetails.bathrooms}\n` +
                (propertyDetails.plotArea ? `• Plot Area: ${propertyDetails.plotArea} m²\n` : '') +
                (propertyDetails.facade ? `• Facade: ${propertyDetails.facade}\n` : '');
              handleDeterministicStep(currentStep, formattedMessage, propertyDetails);
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );
      case 'sales-avm-step4-transaction':
        return (
          <PropertyTransactionForm
            dictionary={dictionary}
            agencyLanguage={urlConfig?.language}
            transactionCount={sellingState.propertyDetails?.transactionCount}
            askAsbestosAndFinishLevel={urlConfig?.askAsbestosAndFinishLevel}
            initialValues={{
              epcValue: sellingState.propertyInfo?.epcValue,
              condition: sellingState.propertyInfo?.condition,
              asbestos: sellingState.propertyInfo?.asbestos,
              finishLevel: sellingState.propertyInfo?.finishLevel,
            }}
            onSubmit={(propertyInfo) => {
              const currentStep = 'sales-avm-step4-transaction';
              setSellingState(prev => ({ ...prev, propertyInfo }));
              const translatedEpcValue = propertyInfo.epcValue === 'unknown' ? (dictionary?.sellingForm?.propertyEpcUnknown || 'Unknown') : propertyInfo.epcValue;
              const conditionKey = `condition${propertyInfo.condition}`; 
              let translatedCondition = dictionary?.propertyForm?.[conditionKey] || propertyInfo.condition;
              // Remove potential duplicated label from the value itself (e.g., "State: 5/5" becomes "5/5")
              if (translatedCondition && translatedCondition.includes(':')) {
                translatedCondition = translatedCondition.split(':').slice(1).join(':').trim();
              }
              
              const finishLevelKeyMap: { [key: string]: string } = {
                'poor': 'finishLevelPoor',
                'moderate': 'finishLevelModerate',
                'good': 'finishLevelGood',
                'very_good': 'finishLevelVeryGood',
              };
              const finishLevelKey = propertyInfo.finishLevel ? finishLevelKeyMap[propertyInfo.finishLevel] : undefined;
              const translatedFinishLevel = finishLevelKey ? (dictionary?.sellingForm?.[finishLevelKey] || propertyInfo.finishLevel) : propertyInfo.finishLevel;

              const translatedPropertyInfo: { [key: string]: any } = {
                [dictionary?.sellingForm?.propertyEpc || 'EPC value']: translatedEpcValue,
                [dictionary?.propertyForm?.condition || 'Condition']: translatedCondition,
              };

              // Only include Finish Level if it was collected
              if (propertyInfo.finishLevel) {
                translatedPropertyInfo[dictionary?.sellingForm?.finishLevel || 'Level of finish'] = translatedFinishLevel;
              }
              
              if (propertyInfo.asbestos) {
                const asbestosKeyMap: { [key: string]: string } = {
                  'none': 'asbestosNone',
                  'not_much': 'asbestosNotMuch',
                  'lots': 'asbestosLots',
                  'dont_know': 'asbestosDontKnow',
                };
                const asbestosKey = asbestosKeyMap[propertyInfo.asbestos];
                const translatedAsbestos = dictionary?.sellingForm?.[asbestosKey] || propertyInfo.asbestos;
                translatedPropertyInfo[dictionary?.sellingForm?.asbestos || 'Asbestos'] = translatedAsbestos;
              }

              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.propertyProvided || 'Here is my property information:'}\n${Object.entries(translatedPropertyInfo)
                  .map(([key, value]) => `• ${key}: ${value}`)
                  .join('\n')}` :
                `Here are my property details:\n${Object.entries(translatedPropertyInfo)
                  .map(([key, value]) => `• ${key}: ${value}`)
                  .join('\n')}`;

              // ---- New Logic for Step 4 Submission ----
              setError(null); // Clear previous error

              // 1. Construct Payload for /api/avm
              // Ensure all necessary data from sellingState is included
              const avmPayload: any = {
                'Postal Code': sellingState.zipCode || '',
                'Living Area': Number(sellingState.propertyDetails?.livingArea) || 0,
                'Building Condition Score': propertyInfo.condition ? parseInt(propertyInfo.condition) : 3,
                'Property Type': sellingState.propertyType || 'house',
                'Address': `${sellingState.streetName} ${sellingState.houseNumber}, ${sellingState.zipCode} ${sellingState.city}`,
                'Plot Surface': sellingState.propertyDetails?.plotArea || 0,
                'New Construction': false, // Assuming false unless captured
                'Energy Class': propertyInfo.epcValue || 'unknown',
                'Number Of Frontages': sellingState.propertyDetails?.facade || (sellingState.propertyType === 'apartment' ? 2 : 4),
                'Bedrooms': sellingState.propertyDetails?.bedrooms || 0,
                'Latitude': sellingState.latitude,
                'Longitude': sellingState.longitude,
                // Add any other required fields for your /api/avm endpoint
              };

              // Only include Asbestos and Finish Level if they were collected
              if (propertyInfo.asbestos) {
                avmPayload['Asbestos'] = propertyInfo.asbestos;
              }
              if (propertyInfo.finishLevel) {
                avmPayload['Finish Level'] = propertyInfo.finishLevel;
              }

              // --- Add a check/log for missing coordinates --- START
              if (sellingState.latitude == null || sellingState.longitude == null) {
                logger.error('CRITICAL: Latitude or Longitude missing from sellingState in Step 7 - preventing OpenAI fallback', {
                  reason: 'Coordinates not available, likely due to session loss or geocoding failure',
                  failure_type: 'coordinate_validation_failure',
                  impact: 'User cannot proceed with final valuation, must restart address entry',
                  latitude: sellingState.latitude,
                  longitude: sellingState.longitude,
                  has_address_data: {
                    streetName: !!sellingState.streetName,
                    houseNumber: !!sellingState.houseNumber,
                    zipCode: !!sellingState.zipCode,
                    city: !!sellingState.city
                  },
                  current_workflow_step: currentStep,
                  chat_id: chatId,
                  guest_id: guestId,
                  agent_id: urlConfig?.agencyId,
                  session_initialized: hasInitialized,
                  // Log minimal state for debugging without exposing PII
                  property_type: sellingState.propertyType,
                  has_property_details: !!sellingState.propertyDetails,
                  has_contact_info: !!sellingState.contactInfo,
                  has_price_range: !!sellingState.priceRange,
                  has_comparables: !!(sellingState.comparables && sellingState.comparables.length > 0)
                });
                sendErrorToSlack({
                  error: {
                    message: 'Latitude or Longitude missing in Step 7 before AVM background fetch'
                  },
                  context: {
                    endpoint: '/app/[locale]/avm/_components/AvmAgent - Step7 AVM background',
                    timestamp: new Date().toISOString(),
                    environment: process.env.NODE_ENV || 'development'
                  },
                  userData: {
                    guestId,
                    chatId,
                    agencyId: urlConfig?.agencyId
                  },
                  requestPayload: {
                    latitude: sellingState.latitude,
                    longitude: sellingState.longitude,
                    streetName: sellingState.streetName,
                    houseNumber: sellingState.houseNumber,
                    zipCode: sellingState.zipCode,
                    city: sellingState.city
                  }
                });
                logger.info('User notified: coordinate validation failure in Step 7 - sent error message to user');
                setError('Location coordinates are missing. Please go back to the address step and try again.');
                setIsLoading(false);
                return;
              }
              // --- Add a check/log for missing coordinates --- END

              // Create the user message object (but don't send yet)
              const newUserMessage: Message = {
                role: 'user',
                content: formattedMessage,
                chatId: chatId,
                userId: guestId,
                classification: { primary_intent: 'provide_data', workflowStep: currentStep },
                workflowStep: 'avm_form'
              };

              // Background task: Save user message, call AVM, update state, call workflow, add next step
              const processStep4 = async () => {
                try {
                  // --- New Flow: Workflow first, then background AVM fetch --- 

                  // Save the user message first
                  if (chatId && guestId) {
                    await saveMessage(newUserMessage);
                  }
                  // Update UI immediately with user message
                  setMessages(prev => [...prev, newUserMessage]);

                  // 1. Call workflow FIRST to get next step ID
                  const workflowResponse = await fetch('/api/avm/workflow', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentStep }),
                  });

                  if (!workflowResponse.ok) {
                    throw new Error(`Workflow API returned ${workflowResponse.status}`);
                  }
                  const { nextStep } = await workflowResponse.json();

                  // 2. Initiate AVM fetch in the background (DO NOT AWAIT)
                  const avmPromise = fetch('/api/avm', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messages: [{ role: 'user', content: JSON.stringify(avmPayload) }] })
                  })
                    .then(async (response) => { // Add basic processing to check status and parse JSON
                      if (!response.ok) {
                        const errorData = await response.json();
                        logger.error('Background AVM fetch failed', { status: response.status, error: JSON.stringify(errorData) });
                        throw new Error(`Background AVM fetch failed: ${response.status} ${JSON.stringify(errorData)}`);
                      }
                      const data = await response.json();
                      // Validate required fields within the promise result
                      if (!data.properties || data.subject_latitude === undefined || data.subject_longitude === undefined) {
                        logger.error('Background AVM fetch response missing required data', { response: data });
                        throw new Error('Background AVM fetch response missing required data.');
                      }
                      return data; // Resolve with the parsed and validated data
                    })
                    .catch(error => {
                      logger.error('Caught error during background AVM fetch promise', { error });
                      // Ensure the promise rejects with an error so await in Step 5 catches it
                      throw error; 
                    });

                  // 3. Store the promise in state
                  setAvmFetchPromise(avmPromise);

                  // 4. Proceed immediately to the next step (Step 5)
                  if (nextStep === 'sales-avm-step5-additional-info') {
                    const nextAssistantMessage: Message = {
                      role: 'assistant',
                      content: '|SHOW_SELLING_FORM',
                      chatId: chatId,
                      userId: agentId as string,
                      classification: { primary_intent: 'sales_avm', workflowStep: nextStep },
                      workflowStep: 'avm_form'
                    };

                    // Show typing dots briefly before rendering Additional Info form
                    const startedAt = Date.now();
                    const typingId = showAssistantTyping();
                    await ensureMinDelay(startedAt);
                    replaceTypingWith(typingId, nextAssistantMessage);
                    // Save the assistant message
                    if (chatId && agentId) {
                      await saveMessage(nextAssistantMessage);
                    }

                    // Persist step: step5 additional info
                    logger.info('[AVM step-update] After step4, moving to step5-additional-info');
                    await updateChatCurrentStep('sales-avm-step5-additional-info');
                  } else {
                    logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} after step 4.`);
                    setError('Could not determine the next step automatically.');
                  }

                } catch (error) {
                  // Error handling now focuses on workflow call or saving message
                  logger.error('Error during Step 4 processing (workflow call or saving message):', error);
                  setError(`Failed to process property details: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  // Optionally add an error message to the chat
                }
              };

              // Start the background processing
              processStep4();
              // ---- End New Logic for Step 4 ----
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step5-additional-info':
        return (
          <AdditionalInfoForm
            dictionary={dictionary}
            initialChoice={typeof sellingState.additionalDetails === 'string' ? (sellingState.additionalDetails.trim().length > 0) : undefined}
            initialDetails={typeof sellingState.additionalDetails === 'string' ? sellingState.additionalDetails : undefined}
            onSubmit={(additionalDetails: string) => {
              const currentStep = 'sales-avm-step5-additional-info';
              const formattedMessage = dictionary?.sellingForm ? 
                additionalDetails ? 
                  `${dictionary.sellingForm.additionalDetailsProvided || 'Provided details:'}\n${additionalDetails}` : 
                  (dictionary?.sellingForm?.noAdditionalDetailsProvided || 'No additional details provided.')
                :
                additionalDetails ?
                  `Here are my additional details:\n${additionalDetails}` :
                  'No additional details provided.';
               
              // --- Update Logic for Step 5 Submission ---
              setIsLoading(true);
              setError(null);

              // Create user message object
              const newUserMessage: Message = {
                role: 'user',
                content: formattedMessage,
                chatId: chatId,
                userId: guestId,
                classification: { primary_intent: 'provide_data', workflowStep: currentStep },
                workflowStep: 'avm_form'
              };

              // --- Add user message immediately ---
              setMessages(prev => [...prev, newUserMessage]);

              // --- Define background task with updated loader logic --- 
              const processStep5 = async (detailsForReasoning: string) => { 
                try {
                  // --- Save user message (can happen concurrently) --- 
                  if (chatId && guestId) {
                    saveMessage(newUserMessage); // Fire and forget for now
                  }

                  // --- Prepare data for Reasoning Loader --- 
                  const city = sellingState.city || '';
                  const propTypeRaw = sellingState.propertyType || 'property';
                  const propType = propTypeRaw === 'house' ? 
                    (dictionary?.sellingForm?.propertyTypeHouse || 'house') : 
                    (dictionary?.sellingForm?.propertyTypeApartment || 'apartment');
                  const livingArea = sellingState.propertyDetails?.livingArea || 0;
                  const bedrooms = sellingState.propertyDetails?.bedrooms;
                  const bathrooms = sellingState.propertyDetails?.bathrooms;
                  const plotArea = sellingState.propertyDetails?.plotArea;

                  // --- Create and Add the SINGLE Reasoning Loader Message --- 
                  const reasoningStartMessage: Message = {
                    role: 'assistant',
                    content: '|SHOW_REASONING_LOADER', // Flag to render the component
                    chatId: chatId,
                    userId: agentId as string,
                    classification: { primary_intent: 'system_reasoning', workflowStep: 'reasoning_start' }, 
                    properties: { 
                      city,
                      propertyType: propType,
                      livingArea,
                      bedrooms,
                      bathrooms,
                      plotArea,
                      additionalDetails: detailsForReasoning
                    },
                    // Assign temporary ID for React key purposes
                    id: `temp-reasoning-${Date.now()}` 
                  };
                  
                  setMessages(prev => [...prev, reasoningStartMessage]);

                  // --- Calculate total wait time --- 
                  const hasAdditionalDetails = detailsForReasoning && detailsForReasoning.trim().length > 0;
                  const numberOfReasoningSteps = hasAdditionalDetails ? 4 : 3;
                  const totalWaitTime = (numberOfReasoningSteps * 1500);



                  // --- Wait for the calculated duration THEN perform background work & update UI --- 
                  setTimeout(async () => {
                    try {
                      // --- Background work ...
                      if (!avmFetchPromise) {
                        throw new Error('AVM fetch promise not found. Cannot proceed.');
                      }
                      const avmData = await avmFetchPromise;

                      setSellingState(prev => ({
                        ...prev,
                        comparables: avmData.properties,
                        subject_latitude: avmData.subject_latitude,
                        subject_longitude: avmData.subject_longitude,
                        additionalDetails: detailsForReasoning 
                      }));

                      const workflowResponse = await fetch('/api/avm/workflow', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ currentStep }),
                      });

                      if (!workflowResponse.ok) {
                        throw new Error(`Workflow API returned ${workflowResponse.status} after awaiting AVM data`);
                      }
                      const { nextStep } = await workflowResponse.json();

                      if (nextStep === 'sales-avm-step6-map-timeframe') {
                        const nextAssistantMessage: Message = {
                          role: 'assistant',
                          content: '|SHOW_SELLING_FORM',
                          chatId: chatId,
                          userId: agentId as string,
                          classification: { primary_intent: 'sales_avm', workflowStep: nextStep },
                          workflowStep: 'avm_form'
                        };
                        // No typing dots after Additional Info or ReasoningLoader
                        setMessages(prev => [...prev, nextAssistantMessage]);
                        if (chatId && agentId) {
                          await saveMessage(nextAssistantMessage);
                        }
                        logger.info('[AVM step-update] After step5, moving to step6-map-timeframe');
                        await updateChatCurrentStep('sales-avm-step6-map-timeframe');
                      } else {
                        // Handle unexpected step, but DON'T remove the reasoning message
                        logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} after step 5.`);
                        setError('Could not determine the next step automatically.');
                      }
                    } catch (error) {
                      // Handle error, but DON'T remove the reasoning message
                      logger.error('Error during Step 5 background processing (awaiting AVM or workflow):', error);
                      setError(`Failed to process additional info: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    } finally {
                      setIsLoading(false);
                      setAvmFetchPromise(null); 
                    }
                  }, totalWaitTime); 

                } catch (outerError) {
                  logger.error('Error in synchronous part of Step 5 processing:', outerError);
                  setError(`An unexpected error occurred: ${outerError instanceof Error ? outerError.message : 'Unknown error'}`);
                  setIsLoading(false); 
                  setAvmFetchPromise(null); 
                }
              };

              // --- Start background processing, passing details --- 
              processStep5(additionalDetails);
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step6-map-timeframe':
        const streetName = sellingState.streetName?.replace(/\s+\d+.*/, '') || '';
        return (
          <MapTimeframeForm
            dictionary={dictionary}
            streetName={streetName}
            comparables={sellingState.comparables || []}
            subject_latitude={sellingState.subject_latitude}
            subject_longitude={sellingState.subject_longitude}
            excludedTimeframeOptions={urlConfig?.excludedTimeframeOptions}
            initialSellingTimeframe={sellingState.sellingTimeframe}
            enableFollowUp={Boolean(urlConfig?.enableTimeframeFollowUp)}
            initialMovePlan={sellingState.movePlan}
            onSubmit={({ sellingTimeframe, movePlan }) => {
              const currentStep = 'sales-avm-step6-map-timeframe';
              setSellingState(prev => ({ ...prev, sellingTimeframe, movePlan }));
              
              // Find the label for the selected timeframe for a more user-friendly message
              const timeframeOptions = [
                { value: 'now', labelKey: 'timeframeNow', default: 'Now' },
                { value: '6m', labelKey: 'timeframe6m', default: 'In 6 months' },
                { value: '12m', labelKey: 'timeframe12m', default: 'In 12 months' },
                { value: 'curious', labelKey: 'timeframeCurious', default: 'Yes, I don\'t know when yet' },
                { value: 'no', labelKey: 'timeframeNo', default: 'No, I don\'t want to sell' },
              ];
              const selectedOption = timeframeOptions.find(o => o.value === sellingTimeframe);
              const timeframeLabel = dictionary?.sellingForm?.[selectedOption?.labelKey || ''] || selectedOption?.default || sellingTimeframe;
              
              let formattedMessage = `${dictionary?.sellingForm?.sellingTimeframeResponseTitle || 'Selling timeframe'}\n• ${timeframeLabel}`;
              if (movePlan) {
                const labels: Record<string, string> = {
                  sell_then_buy: dictionary?.sellingForm?.movePlanSellThenBuy || 'I want to sell first and buy after',
                  bought_then_sell: dictionary?.sellingForm?.movePlanBoughtThenSell || 'We already bought and want to sell',
                  sell_only: dictionary?.sellingForm?.movePlanSellOnly || 'We only want to sell',
                  buy_then_sell: dictionary?.sellingForm?.movePlanBuyThenSell || 'We want to buy first and then sell'
                };
                formattedMessage += `\n• ${labels[movePlan]}`;
              }

              handleDeterministicStep(currentStep, formattedMessage, { sellingTimeframe });
              logger.info('[AVM step-update] After step6, moving to step7-contact');
              updateChatCurrentStep('sales-avm-step7-contact');
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step7-contact':
        
        const contactPrompt = (urlConfig?.contactPromptOverrideKey && dictionary?.sellingForm?.[urlConfig.contactPromptOverrideKey])
          ? dictionary.sellingForm[urlConfig.contactPromptOverrideKey]
          : dictionary?.sellingForm?.contactPrompt || 'Please provide your contact information';

        return (
          <ContactForm
            dictionary={dictionary}
            contactPrompt={contactPrompt}
            showNameInput={true}
            agencyId={urlConfig?.agencyId}
            askEmail={urlConfig?.askEmail}
            initialContactInfo={sellingState.contactInfo}
            onSubmit={async (contactInfo: ContactInfo) => {
              const currentStep = 'sales-avm-step7-contact';
              setSellingState(prev => ({ ...prev, contactInfo })); // contactInfo now includes name
              // Updated formatted message to include name, email, and phone in the correct order
              const details = [];
              if (contactInfo.name) {
                details.push(`• ${dictionary?.sellingForm?.contactName || 'Name'}: ${contactInfo.name}`);
              }
              if (contactInfo.email) {
                details.push(`• ${dictionary?.sellingForm?.contactEmail || 'Email'}: ${contactInfo.email}`);
              }
              details.push(`• ${dictionary?.sellingForm?.contactPhone || 'Phone'}: ${contactInfo.phone}`);
              const contactDetailsString = details.join('\n');

              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.contactProvided || 'Here is my contact information:'}\n${contactDetailsString}` :
                `Here is my contact information:\n${contactDetailsString}`;

              // --- Logic moved from old step 6 ---
              setIsLoading(true);
              setError(null);

              const newUserMessage: Message = {
                role: 'user',
                content: formattedMessage,
                chatId: chatId,
                userId: guestId,
                classification: { primary_intent: 'provide_data', workflowStep: currentStep },
                workflowStep: 'avm_form'
              };

              const processStep7 = async () => {
                try {
                  if (chatId && guestId) {
                    await saveMessage(newUserMessage);
                  }
                  setMessages(prev => [...prev, newUserMessage]);


                  const subjectProperty: any = { 
                    'Postal Code': sellingState.zipCode || '',
                    'Living Area': Number(sellingState.propertyDetails?.livingArea) || 0,
                    'Building Condition Score': sellingState.propertyInfo?.condition ? parseInt(sellingState.propertyInfo.condition) : 3,
                    'Property Type': sellingState.propertyType || 'house',
                    'Address': `${sellingState.streetName} ${sellingState.houseNumber}, ${sellingState.zipCode} ${sellingState.city}`,
                    'Plot Surface': sellingState.propertyDetails?.plotArea || 0,
                    'New Construction': false,
                    'Energy Class': sellingState.propertyInfo?.epcValue || 'unknown',
                    'Number Of Frontages': sellingState.propertyDetails?.facade || (sellingState.propertyType === 'apartment' ? 2 : 4),
                    'Bedrooms': sellingState.propertyDetails?.bedrooms || 0
                  };

                  // Only include Asbestos and Finish Level if they were collected
                  if (sellingState.propertyInfo?.asbestos) {
                    subjectProperty['Asbestos'] = sellingState.propertyInfo.asbestos;
                  }
                  if (sellingState.propertyInfo?.finishLevel) {
                    subjectProperty['Finish Level'] = sellingState.propertyInfo.finishLevel;
                  }

                  const calculatePayload = {
                    comparables: sellingState.comparables || [],
                    additionalDetails: sellingState.additionalDetails || null,
                    subjectProperty,
                  };

                  const calculateResponse = await fetch('/api/avm/calculate-value', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(calculatePayload)
                  });

                  if (!calculateResponse.ok) {
                    const errorData = await calculateResponse.json();
                    throw new Error(`Calculate Value API failed with status ${calculateResponse.status}. ${JSON.stringify(errorData)}`);
                  }
                  const calculationData = await calculateResponse.json();

                  if (!calculationData.priceRange || !calculationData.priceRange.min || !calculationData.priceRange.max) {
                    logger.error('Calculate Value API response missing priceRange', { response: calculationData });
                    throw new Error('Calculate Value API response missing priceRange.');
                  }
                  


                  const possibleRatings = [4, 4.5, 5];
                  const randomIndex = Math.floor(Math.random() * possibleRatings.length);
                  const calculatedStarRating = possibleRatings[randomIndex];
                  
                  setSellingState(prev => ({
                    ...prev,
                    priceRange: calculationData.priceRange,
                    starRating: calculatedStarRating,
                    contactInfo: contactInfo
                  }));

                  const workflowResponse = await fetch('/api/avm/workflow', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentStep }),
                  });

                  if (!workflowResponse.ok) {
                    throw new Error(`Workflow API returned ${workflowResponse.status} after calculating value`);
                  }
                  const { nextStep } = await workflowResponse.json();

                  if (nextStep === 'sales-avm-step8-valuation') {
                    const minPrice = calculationData.priceRange.min;
                    const maxPrice = calculationData.priceRange.max;
                    const valuationTemplate = dictionary?.sellingForm?.valuationMessageContent || 
                      'Based on the details, the estimated value is €[MIN_PRICE] - €[MAX_PRICE].';
                    const valuationContent = valuationTemplate
                      .replace('[MIN_PRICE]', minPrice.toLocaleString(['nl-BE']))
                      .replace('[MAX_PRICE]', maxPrice.toLocaleString(['nl-BE']))
                      + ' |SHOW_SELLING_FORM';

                    const nextAssistantMessage: Message = {
                      role: 'assistant',
                      content: valuationContent,
                      chatId: chatId,
                      userId: agentId as string,
                      classification: { primary_intent: 'sales_avm', workflowStep: nextStep },
                      properties: {
                        priceRange: calculationData.priceRange
                      }
                    };
                    const startedAt = Date.now();
                    const typingId = showAssistantTyping();
                    await ensureMinDelay(startedAt);
                    replaceTypingWith(typingId, nextAssistantMessage);
                    logger.info('[AVM step-update] After step7, moving to step8-valuation');
                    await updateChatCurrentStep('sales-avm-step8-valuation');
                  } else {
                    logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} after step 7.`);
                    const nextAssistantMessage: Message = {
                      role: 'assistant',
                      content: '|SHOW_SELLING_FORM',
                      chatId: chatId,
                      userId: agentId as string,
                      classification: { primary_intent: 'sales_avm', workflowStep: nextStep || 'unknown' },
                      workflowStep: 'avm_form'
                    };
                    const startedAt = Date.now();
                    const typingId = showAssistantTyping();
                    await ensureMinDelay(startedAt);
                    replaceTypingWith(typingId, nextAssistantMessage);
                    if (chatId && agentId) {
                      await saveMessage(nextAssistantMessage);
                    }
                    if (!nextStep) {
                      setError('Could not determine the next step automatically.');
                    }
                  }

                  if (isPhoneVerified) {
                    await triggerAvmEmailNotification();
                  }

                } catch (error) {
                  logger.error('Error during Step 7 processing (Calculate Value or workflow):', error);
                  setError(`Failed to process contact information: ${error instanceof Error ? error.message : 'Unknown error'}`);
                } finally {
                  setIsLoading(false);
                }
              };

              processStep7();
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step8-valuation':
        
        const handleEmailSubmit = (email: string) => {
          setIsFlowCompleted(true);
        
          // This is a fire-and-forget background task.
          (async () => {
            try {
              const currentState = sellingStateRef.current;
              const updatedContactInfo = {
                ...currentState.contactInfo,
                phone: currentState.contactInfo?.phone || '',
                email: email,
              };
        
              // --- Payload for /api/avm/trigger (if timer was pending) ---
              const { comparables, ...sellingStateWithoutComparables } = currentState;
              const triggerPayload = {
                locale: locale,
                agencyId: urlConfig?.agencyId,
                chatId: chatId,
                guestId: guestId,
                sellingState: {
                  ...sellingStateWithoutComparables,
                  contactInfo: updatedContactInfo,
                },
              };
        
              // --- Payload for /api/avm/trigger-email (only for standard email collection) ---
              const guestEmailPayload = urlConfig?.enableEmailCollection ? {
                locale: locale,
                agencyId: urlConfig?.agencyId,
                agencyName: urlConfig?.agencyName,
                agencyLanguage: urlConfig?.agencyLanguage,
                sellingState: {
                  streetName: currentState.streetName,
                  houseNumber: currentState.houseNumber,
                  zipCode: currentState.zipCode,
                  city: currentState.city,
                  priceRange: currentState.priceRange,
                  contactInfo: { email: email },
                  comparables: currentState.comparables,
                  subject_latitude: currentState.subject_latitude,
                  subject_longitude: currentState.subject_longitude,
                },
              } : null;
        
              // --- Payload for /api/avm/update-lead-contact (if timer already fired) ---
              const updatePayload = {
                guestId: guestId,
                chatId: chatId,
                agencyId: urlConfig?.agencyId,
                locale: locale,
                contactInfo: updatedContactInfo,
              };
              
              // --- Use unified orchestrator action for all email collection types ---
              const action = 'complete_with_update';
              
              // Determine update type and guest email settings
              let updateType: string;
              let shouldSendGuestEmail: boolean;
              
              if (urlConfig?.enableEmailCollectionOnlyAgency) {
                // For agency001: Agency-only email collection
                updateType = 'custom_email';
                shouldSendGuestEmail = false;
              } else {
                // For agency023: Standard email collection with guest email
                updateType = 'email';
                shouldSendGuestEmail = true;
              }
              
              const response = await fetch('/api/avm/orchestrate-post-verification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action,
                  data: {
                    guestId,
                    updateType,
                    shouldSendGuestEmail,
                    triggerPayload,
                    guestEmailPayload,
                    updatePayload,
                  },
                }),
              });
              
              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Orchestrator call failed: ${errorText}`);
              }
              
              logger.info(`Successfully processed ${updateType} update`);
        
              // --- Silently save user/assistant messages for record-keeping ---
              await saveMessage({
                role: 'user',
                content: `Email: ${email}`,
                chatId: chatId,
                userId: guestId,
                workflowStep: 'avm_form'
              });
              await saveMessage({
                role: 'assistant',
                content: '|AVM_FLOW_COMPLETE',
                chatId: chatId,
                userId: agentId as string,
                workflowStep: 'avm_form'
              });
        

            } catch (error) {
              logger.error('Error during orchestrated email submission process:', error);
            }
          })();
        };

        const handleCustomValuationSubmit = () => {
          setIsFlowCompleted(true);

          // Background task
          (async () => {
            try {
              const currentState = sellingStateRef.current;
              const updatedContactInfo = {
                ...currentState.contactInfo,
                phone: currentState.contactInfo?.phone || '',
              };

              // Payload for /api/avm/trigger
              const { comparables, ...sellingStateWithoutComparables } = currentState;
              const triggerPayload = {
                locale: locale,
                agencyId: urlConfig?.agencyId,
                chatId: chatId,
                guestId: guestId,
                userDescription: 'Wants an in-person valuation report', // Add user description for comprehensive email
                sellingState: {
                  ...sellingStateWithoutComparables,
                  contactInfo: updatedContactInfo,
                },
              };

              // Payload for /api/avm/update-lead-description
              const updatePayload = {
                guestId: guestId,
                chatId: chatId,
                agencyId: urlConfig?.agencyId,
                locale: locale,
                contactInfo: updatedContactInfo,
                description: 'Wants an in-person valuation report'
              };

              // Use unified orchestrator action for custom valuation
              const action = 'complete_with_update';
              
              const response = await fetch('/api/avm/orchestrate-post-verification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action,
                  data: {
                    guestId,
                    updateType: 'custom_valuation',
                    shouldSendGuestEmail: false,
                    triggerPayload,
                    updatePayload,
                  },
                }),
              });

              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Orchestrator call failed: ${errorText}`);
              }

              // Save messages for record-keeping
              await saveMessage({
                role: 'user',
                content: 'I want an in-person valuation report',
                chatId: chatId,
                userId: guestId,
                workflowStep: 'avm_form'
              });
              await saveMessage({
                role: 'assistant',
                content: '|CUSTOM_VALUATION_COMPLETE',
                chatId: chatId,
                userId: agentId as string,
                workflowStep: 'avm_form'
              });

            } catch (error) {
              logger.error('Error during custom valuation submission:', error);
            }
          })();
        };

        const handleVerificationSuccess = () => {
          // Mark as completed (step 9) when phone is verified and value can be shown
          logger.info('[AVM step-update] Phone verification success -> marking step9');
          updateChatCurrentStep('sales-avm-step9-final-evaluation');
        
          if (urlConfig?.enableEmailCollection) {
            
            // This is an async fire-and-forget operation from the user's perspective.
            (async () => {
              try {
                // Ensure guestId is available before proceeding
                if (!guestId) {
                  logger.error('Cannot schedule notification: guestId is missing.');
                  return;
                }

                const currentState = sellingStateRef.current;
                const { comparables, ...sellingStateWithoutComparables } = currentState;
                const sellingStateToSend = {
                  ...sellingStateWithoutComparables,
                  contactInfo: currentState.contactInfo
                };
        
                const triggerPayload = {
                  locale: locale,
                  agencyId: urlConfig?.agencyId,
                  chatId: chatId,
                  guestId: guestId,
                  sellingState: sellingStateToSend,
                };
        
                const response = await fetch('/api/avm/orchestrate-post-verification', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    action: 'schedule',
                    data: {
                      guestId, // Pass guestId at the top level for the orchestrator to use as a key
                      triggerPayload,
                    },
                  }),
                });
        
                if (!response.ok) {
                  const errorText = await response.text();
                  throw new Error(`Failed to schedule notification: ${errorText}`);
                }

              } catch (error) {
                logger.error('Error scheduling server-side notification:', error);
                // Optional: inform user of background failure
              }
            })();
          } else if (urlConfig?.enableEmailCollectionOnlyAgency) {
            
            // For agency001: Schedule timer as fallback, but don't send guest email
            // This is an async fire-and-forget operation from the user's perspective.
            (async () => {
              try {
                // Ensure guestId is available before proceeding
                if (!guestId) {
                  logger.error('Cannot schedule notification: guestId is missing.');
                  return;
                }

                const currentState = sellingStateRef.current;
                const { comparables, ...sellingStateWithoutComparables } = currentState;
                const sellingStateToSend = {
                  ...sellingStateWithoutComparables,
                  contactInfo: currentState.contactInfo
                };
        
                const triggerPayload = {
                  locale: locale,
                  agencyId: urlConfig?.agencyId,
                  chatId: chatId,
                  guestId: guestId,
                  sellingState: sellingStateToSend,
                };
        
                const response = await fetch('/api/avm/orchestrate-post-verification', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    action: 'schedule',
                    data: {
                      guestId, // Pass guestId at the top level for the orchestrator to use as a key
                      triggerPayload,
                    },
                  }),
                });
        
                if (!response.ok) {
                  const errorText = await response.text();
                  throw new Error(`Failed to schedule notification: ${errorText}`);
                }

              } catch (error) {
                logger.error('Error scheduling server-side notification:', error);
                // Optional: inform user of background failure
              }
            })();
            
          } else if (urlConfig?.enableCustomValuationComponent) {
            
            // For agency001 with custom valuation component: Schedule custom timer
            (async () => {
              try {
                // Ensure guestId is available before proceeding
                if (!guestId) {
                  logger.error('Cannot schedule custom valuation notification: guestId is missing.');
                  return;
                }

                const currentState = sellingStateRef.current;
                const { comparables, ...sellingStateWithoutComparables } = currentState;
                const sellingStateToSend = {
                  ...sellingStateWithoutComparables,
                  contactInfo: currentState.contactInfo
                };
        
                const triggerPayload = {
                  locale: locale,
                  agencyId: urlConfig?.agencyId,
                  chatId: chatId,
                  guestId: guestId,
                  sellingState: sellingStateToSend,
                };
        
                const response = await fetch('/api/avm/orchestrate-post-verification', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    action: 'schedule_custom_valuation',
                    data: {
                      guestId, // Pass guestId at the top level for the orchestrator to use as a key
                      triggerPayload,
                      customTimer: urlConfig?.customValuationTimer || 60000 // Pass custom timer with fallback
                    },
                  }),
                });
        
                if (!response.ok) {
                  const errorText = await response.text();
                  throw new Error(`Failed to schedule custom valuation notification: ${errorText}`);
                }

              } catch (error) {
                logger.error('Error scheduling custom valuation notification:', error);
                // Optional: inform user of background failure
              }
            })();
            
          } else if (urlConfig?.enableValuationConfirmation) {
            // For agency001 with valuation confirmation: DON'T send email yet
            // Email will be sent when user answers the confirmation question
            logger.info('[AVM] Valuation confirmation enabled - waiting for user response before sending email');
            
          } else {
            // The existing function works perfectly for immediate triggers.
            triggerAvmEmailNotification();
          }
        };

        const handleDetailsResponse = (response: 'result') => {

          // --- Construct the content WITH the price range using dictionary ---
          const minPrice = sellingState.priceRange?.min;
          const maxPrice = sellingState.priceRange?.max;
          // Use a template string from the dictionary if available
          const valuationTemplate = dictionary?.sellingForm?.valuationMessageContent || 
          'Based on the details, the estimated value is €[MIN_PRICE] - €[MAX_PRICE].';
          const valuationContent = valuationTemplate
            .replace('[MIN_PRICE]', minPrice?.toLocaleString(['nl-BE']) || 'N/A') // Use component's locale state
            .replace('[MAX_PRICE]', maxPrice?.toLocaleString(['nl-BE']) || 'N/A') // Use component's locale state
          + ' |SHOW_SELLING_FORM'; // Append the flag

          const primaryIntent = 'valuation_result';

          const silentUserMessage: Message = {
            role: 'assistant',
            content: valuationContent, // <-- Use the new content with price range
            chatId: chatId,
            userId: agentId as string,
            classification: { 
              primary_intent: primaryIntent, 
              workflowStep: 'sales-avm-step8-valuation' 
            },
            workflowStep: 'avm_form',
            // Store raw price range in properties for easier backend access if needed
            properties: {
              priceRange: sellingState.priceRange
            }
          };
          // Save the message but DO NOT add it to the messages state
          saveMessage(silentUserMessage);
        };
        
        return (
          <div className="valuation-form-interaction-area">
            <ValuationForm
              dictionary={dictionary}
              phoneNumber={sellingState.contactInfo?.phone || ''}
              priceRange={sellingState.priceRange}
              // --- Pass verification state and setter ---
              isVerified={isPhoneVerified}
              setIsVerified={setIsPhoneVerified}
              // --- Pass star rating from state --- 
              initialStarRating={sellingState.starRating}
              // --- Pass email collection configuration ---
              enableEmailCollection={urlConfig?.enableEmailCollection ?? false}
              enableEmailCollectionOnlyAgency={urlConfig?.enableEmailCollectionOnlyAgency ?? false}
              customEmailMessage={urlConfig?.customEmailMessage}
              customEmailPrompt={urlConfig?.customEmailPrompt}
              customThankYouMessage={urlConfig?.customThankYouMessage}
              // --- Pass custom valuation component configuration ---
              enableCustomValuationComponent={urlConfig?.enableCustomValuationComponent ?? false}
              customValuationMessage={urlConfig?.customValuationMessage}
              customValuationButton={urlConfig?.customValuationButton}
              customValuationTimer={urlConfig?.customValuationTimer}
              onCustomValuationSubmit={() => {
                handleCustomValuationSubmit();
              }}
              // --- Pass agent contact notice control ---
              agentContactNoticeNull={urlConfig?.agentContactNoticeNull ?? false}
              // --- Pass locale for API calls ---
              locale={locale}
              // --- Pass valuation confirmation configuration ---
              enableValuationConfirmation={urlConfig?.enableValuationConfirmation ?? false}
              initialValuationConfirmed={sellingState.valuationConfirmed ?? null}
              // --- Pass no valuation configuration ---
              noValuation={urlConfig?.noValuation ?? false}
              onValuationConfirmationResponse={(wantsContact) => {
                // Update state for persistence
                setSellingState(prev => ({ ...prev, valuationConfirmed: wantsContact }));
                
                // Background API call to trigger email with valuation confirmation included
                (async () => {
                  try {
                    const currentState = sellingStateRef.current;
                    
                    logger.info('[AVM] User answered valuation confirmation - triggering email to agency');
                    
                    const { comparables, ...sellingStateWithoutComparables } = currentState;
                    const sellingStateToSend = {
                      ...sellingStateWithoutComparables,
                      contactInfo: currentState.contactInfo,
                      valuationConfirmed: wantsContact // Include the confirmation in the state
                    };
                    
                    const triggerPayload = {
                      locale: locale,
                      agencyId: urlConfig?.agencyId,
                      agentId: urlConfig?.agentId,
                      chatId: chatId,
                      guestId: guestId,
                      sellingState: sellingStateToSend,
                    };
                    
                    const response = await fetch('/api/avm/trigger', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(triggerPayload)
                    });
                    
                    if (!response.ok) {
                      const errorText = await response.text();
                      logger.error(`Failed to trigger AVM email after valuation confirmation: ${errorText}`);
                    } else {
                      logger.info('[AVM] Successfully sent email to agency after valuation confirmation');
                    }
                    
                  } catch (error) {
                    logger.error('Error triggering email after valuation confirmation:', error);
                  }
                })();
              }}
              // --- Add onGoBack prop --- 
              onGoBack={() => {
                
                setMessages(prevMessages => {
                  // 1. Identify indices to remove
                  const lastMessageIndex = prevMessages.length - 1; // Index of current Step 8 trigger
                  let lastStep7AssistantIndex = -1;
                  let lastStep7UserAnswerIndex = -1;

                  // Find the index of the most recent previous Step 7 assistant trigger
                  for (let i = lastMessageIndex - 1; i >= 0; i--) { // Start search before the last message
                    if (prevMessages[i].role === 'assistant' && prevMessages[i].classification?.workflowStep === 'sales-avm-step7-contact') {
                      lastStep7AssistantIndex = i;
                      // Now find the user answer immediately following it
                      if (i + 1 < lastMessageIndex && 
                          prevMessages[i + 1].role === 'user' && 
                          prevMessages[i + 1].classification?.workflowStep === 'sales-avm-step7-contact') {
                        lastStep7UserAnswerIndex = i + 1;
                      } else {
                        logger.warn('Could not find user answer immediately after the last Step 7 assistant message.');
                      }
                      break; // Found the latest one
                    }
                  }

                  // 2. Filter out the identified messages
                  const filteredMessages = prevMessages.filter((_, index) => 
                    index !== lastMessageIndex && 
                    index !== lastStep7AssistantIndex &&
                    index !== lastStep7UserAnswerIndex
                  );

                  // 3. Create the new Step 7 message
                  const step7Message: Message = {
                    role: 'assistant',
                    content: '|SHOW_SELLING_FORM', 
                    chatId: chatId,
                    userId: agentId as string,
                    classification: {
                      primary_intent: 'sales_avm',
                      workflowStep: 'sales-avm-step7-contact'
                    },
                    workflowStep: 'avm_form'
                  };

                  // 4. Return the new array
                  return [...filteredMessages, step7Message]; 
                });
              }}
              // --- Pass trigger function for successful verification --- 
              onVerificationSuccess={handleVerificationSuccess}
              // --- Pass the new handler ---
              onDetailsResponse={handleDetailsResponse} 
              onEmailSubmit={handleEmailSubmit} 
              // --- Pass completion state to persist the success message ---
              isSubmitted={isFlowCompleted}
            />
            {/* --- Comment out the Continue button for Step 8 --- */}
            {/* 
            <button
              onClick={() => {
                const currentStep = 'sales-avm-step8-valuation';
                const confirmationMessage = dictionary?.sellingForm?.valuationConfirmed || 'Thank you for confirming.';
                handleDeterministicStep(currentStep, confirmationMessage, sellingState.priceRange);
              }}
              disabled={isLoading}
              className={`w-fit bg-zinc-900 text-white py-2 px-6 rounded-lg transition-colors mt-4 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-800'}`}
            >
              {dictionary?.sellingForm?.continueButton || 'Continue'}
            </button>
            */}
          </div>
        );

      case 'sales-avm-step9-final-evaluation':
        return null;

      default:
        return null;
    }
  };

  // Ensure we can imperatively scroll to bottom on open/mount/resume
  const scrollToBottomNow = useCallback(() => {
    try {
      const container = messagesContainerRef.current;
      if (!container) return;
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    } catch (_) {}
  }, []);

  // --- Session snapshot helpers ---
  const buildSessionSnapshot = useCallback(() => {
    try {
      // Derive current workflow step from latest assistant form message
      const lastAssistantForm = [...messages]
        .reverse()
        .find(m => m.role === 'assistant' && m.content?.includes('|SHOW_SELLING_FORM') && m.classification?.workflowStep);
      const workflowStep = lastAssistantForm?.classification?.workflowStep;
      const snapshot = {
        chatId,
        guestId,
        agentId,
        currentWorkflowStep: workflowStep,
        sellingState,
        isPhoneVerified,
        isFlowCompleted,
      };
      return snapshot;
    } catch (_) {
      return null as any;
    }
  }, [chatId, guestId, agentId, messages, sellingState, isPhoneVerified, isFlowCompleted]);

  // Removed unused requestSessionSnapshotFromParent (we inline the request when ready)

  // Removed parent snapshot listener – state is kept within the component only

  // Determine current step for progress bar
  const lastFormMessage = messages
    .slice()
    .reverse()
    .find(m => m.role === 'assistant' && m.content?.includes('|SHOW_SELLING_FORM') && m.classification?.workflowStep);

  const currentWorkflowStep = lastFormMessage?.classification?.workflowStep;
  const DISPLAY_TOTAL_STEPS = 8; // Still aiming for 8 visual steps

  let progressPercent = 0;
  let currentStepNumber = 0;
  let totalDisplaySteps = DISPLAY_TOTAL_STEPS;

  if (currentWorkflowStep === 'sales-avm-rent-contact') {
    progressPercent = 100;
    currentStepNumber = 2;
    totalDisplaySteps = 2;
  } else if (currentWorkflowStep) {
    const stepIndex = AVM_STEPS.indexOf(currentWorkflowStep); // Index in the updated 9-step array

    if (stepIndex !== -1) {
      // Map the 9 logical steps (0-8) to 8 visual steps (1-8)
      // Step 0 (Intro) -> Visual Step 1
      // Step 1 (Address) -> Visual Step 2
      // Step 2 (Details) -> Visual Step 3
      // Step 3 (Transaction) -> Visual Step 4
      // Step 4 (Additional Info) -> Visual Step 5
      // Step 5 (Map+Timeframe) -> Visual Step 6
      // Step 6 (Contact) -> Visual Step 7
      // Step 7 (Valuation) -> Visual Step 8
      // Step 8 (Final Eval) -> Visual Step 8 (Completed)

      currentStepNumber = stepIndex + 1; // Start with 1-based index

      // Cap the displayed step number at DISPLAY_TOTAL_STEPS (8)
      if (currentStepNumber > DISPLAY_TOTAL_STEPS) {
        currentStepNumber = DISPLAY_TOTAL_STEPS;
      }

      // Calculate percentage based on the capped step number
      progressPercent = (currentStepNumber / DISPLAY_TOTAL_STEPS) * 100;

    } else if (currentWorkflowStep === 'completed') { // Final state after step 9 logic
      progressPercent = 100;
      currentStepNumber = DISPLAY_TOTAL_STEPS; // Show 8/8
    }
  }

  // --- Add triggerAvmEmailNotification function (adapted from BasicAgentv0) ---
  const triggerAvmEmailNotification = useCallback(async (contactInfoOverride?: ContactInfo) => {

    const currentState = sellingStateRef.current;
    const finalContactInfo = contactInfoOverride || currentState.contactInfo;

    try {
      // Prepare payload for the new endpoint
      const { comparables, ...sellingStateWithoutComparables } = currentState;
      
      // Ensure contactInfo in the payload includes the name
      const sellingStateToSend = {
        ...sellingStateWithoutComparables,
        contactInfo: finalContactInfo
      };

      const payload = {
        locale: locale, // Pass current locale
        agencyId: urlConfig?.agencyId, // Pass agency ID from config
        agentId: urlConfig?.agentId, // Pass agent ID from config
        chatId: chatId, // <-- Add chatId from state
        guestId: guestId, // <-- ADD THIS
        sellingState: sellingStateToSend // Pass the state object WITHOUT comparables, but WITH name in contactInfo
      };

      // --- Check if agencyId is present ---
      if (!payload.agencyId) {
        logger.error('Cannot trigger AVM email: agencyId is missing from urlConfig.');
        // Optionally inform the user or fallback differently?
        // setError("Configuration error: Missing agency identifier.");
        return; // Stop if no agencyId
      }



      const response = await fetch('/api/avm/trigger', { // Use the new endpoint
        method: 'POST', // Ensure method is POST
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload) // Send the collected data
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Error triggering AVM email: API responded with ${response.status}`, { errorText });
        // Optionally show an error to the user?
        // setError("Failed to send valuation summary email.");
      } else {
        const result = await response.json();
      }
    } catch (error) {
      logger.error('Error calling /api/avm/trigger fetch:', error);
      // Optionally show an error to the user?
      // setError("Failed to send valuation summary email.");
    }
  }, [locale, urlConfig, chatId, guestId]); // Add dependencies: sellingState, locale, urlConfig, chatId, guestId

  // --- Chat step tracking helpers ---
  const getStepNumberForWorkflow = useCallback((workflowStep: string | undefined): number | undefined => {
    if (!workflowStep) return undefined;
    const idx = AVM_STEPS.indexOf(workflowStep);
    return idx >= 0 ? idx + 1 : undefined;
  }, []);

  const updateChatCurrentStep = useCallback(async (workflowStep: string | undefined, chatIdOverride?: string) => {
    try {
      if (!workflowStep) {
        logger.warn('[AVM step-update] workflowStep missing, skipping update');
        return;
      }
      const targetChatId = chatIdOverride || chatId;
      if (!targetChatId) {
        logger.warn('[AVM step-update] chatId missing, cannot persist step', { workflowStep });
        return;
      }
      const stepNumber = getStepNumberForWorkflow(workflowStep);
      // Only include parentUrl when we are persisting step 1, step 2, or rent contact
      const includeParentUrl = workflowStep === 'sales-avm-step1-intro' || workflowStep === 'sales-avm-step2-address' || workflowStep === 'sales-avm-rent-contact';
      const parentUrlToSave = sellingState.leadSourceData?.sourceUrl || leadSourceDataFromWidget?.sourceUrl || urlConfig?.parentUrl || undefined;
      const deviceTypeToSave = sellingState.leadSourceData?.deviceType || leadSourceDataFromWidget?.deviceType || undefined;
      const browserToSave = sellingState.leadSourceData?.browser || leadSourceDataFromWidget?.browser || undefined;

      const payload: any = { currentStepNumber: stepNumber };
      if (includeParentUrl && parentUrlToSave) {
        payload.parentUrl = parentUrlToSave;
        if (deviceTypeToSave) payload.deviceType = deviceTypeToSave;
        if (browserToSave) payload.browser = browserToSave;
      }
      logger.info('[AVM step-update] Persisting step', {
        chatId: targetChatId,
        workflowStep,
        stepNumber,
        includeParentUrl,
        hasParentUrl: Boolean(parentUrlToSave),
        payload
      });
      await fetch(`/api/conversations/${targetChatId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      logger.info('[AVM step-update] Persist success');
    } catch (_) {}
  }, [chatId, getStepNumberForWorkflow, sellingState.leadSourceData, leadSourceDataFromWidget, urlConfig]);

  // --- Assistant typing helpers ---
  const MIN_ASSISTANT_DELAY_MS = 2000;
  const TYPING_APPEAR_DELAY_MS = 300; // avoid showing typing bubble at exact same time as user msg

  const ensureMinDelay = useCallback(async (startedAt: number, minMs: number = MIN_ASSISTANT_DELAY_MS) => {
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, minMs - elapsed);
    if (remaining > 0) {
      await new Promise(resolve => setTimeout(resolve, remaining));
    }
  }, []);

  const showAssistantTyping = useCallback((): string => {
    const typingId = `typing-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const typingMessage: Message = {
      role: 'assistant',
      content: '|ASSISTANT_TYPING',
      chatId: chatId,
      userId: (agentId as string) || undefined,
      id: typingId,
      classification: { primary_intent: 'system_typing', workflowStep: currentWorkflowStep }
    } as Message;
    // Defer insertion slightly so it doesn't appear simultaneously with the user's message
    setTimeout(() => {
      setMessages(prev => {
        // Only prevent duplicate insertion of the same typingId
        const exists = prev.some(m => m.id === typingId);
        if (exists) return prev;
        return [...prev, typingMessage];
      });
    }, TYPING_APPEAR_DELAY_MS);
    return typingId;
  }, [chatId, agentId, currentWorkflowStep]);

  const replaceTypingWith = useCallback((typingId: string, finalMessage: Message) => {
    setMessages(prev => {
      const idx = prev.findIndex(m => m.id === typingId && m.content === '|ASSISTANT_TYPING');
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = finalMessage;
        return next;
      }
      return [...prev, finalMessage];
    });
  }, []);

  const hideAssistantTyping = useCallback((typingId: string) => {
    setMessages(prev => prev.filter(m => m.id !== typingId));
  }, []);

  useEffect(() => {
  }, [locale]);

  // Create a function to send resize message
  const sendResizeMessage = useCallback((newWidth?: string, newHeight?: string) => {
    // Set dimensions based on viewport and chat state
    let width = newWidth;
    let height = newHeight;

    if (!newWidth && !newHeight) {
      if (isMinimized) {
        width = '80px';
        height = '100px';
      } else if (isChatOpen) {
        width = '780px';
        height = '95%';
      } else {
        // Closed state - single widget height for all devices
        width = '425px';
        height = '160px';
      }
    }
      
    // Use requestAnimationFrame to prevent flickering
    requestAnimationFrame(() => {
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
      
      const payload: any = {
        type: 'RESIZE',
        width,
        height,
        isChatOpen,
        isMinimized,
        isTransitioning: isMobile && isTransitioning // Add mobile transition flag
      };
      
      // Hint the parent about the desired visible content width for clipping
      // This does not change the widget size; it's only used for iframe clipping to avoid overlay.
      try {
        if (!isMinimized && isChatOpen && typeof window !== 'undefined' && window.innerWidth > 768) {
          payload.contentWidth = '440px';
        } else if (isMinimized && typeof window !== 'undefined' && window.innerWidth <= 768) {
          // Mobile minimized state: clip to circular button size (56px + small margin)
          payload.contentWidth = '80px';
        }
      } catch (_) {}

      window.parent.postMessage(payload, '*');
    });
  }, [isChatOpen, isMinimized, isTransitioning]);

  useEffect(() => {
    // Send resize message when chat state changes
    sendResizeMessage();
  }, [sendResizeMessage]);

  // Clean up transition state after mobile transition completes
  useEffect(() => {
    if (isTransitioning) {
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
      if (isMobile) {
        // Mobile transition cleanup after 600ms (stages + buffer)
        const cleanupTimer = setTimeout(() => {
          setIsTransitioning(false);
        }, 600);
        
        return () => clearTimeout(cleanupTimer);
      }
    }
  }, [isTransitioning]);

  // Add this near your other useEffect hooks
  useEffect(() => {
    // Function to handle messages from parent
    const handleParentMessage = (event: MessageEvent) => {
      // --- Add guard to prevent reset if flow is completed ---
      if (isFlowCompleted) {
        return;
      }
      if (event.data.type === 'URL_CHANGED') {
        // The first URL_CHANGED message is our signal to proceed with rendering.
        /* This is no longer needed as we removed the initialUrlProcessed gate
        if (!initialUrlProcessed) {
          setInitialUrlProcessed(true);
        }
        */

        // Handle URL change
        const newUrl = event.data.url;

        // Capture successData if provided (this contains the correct parent page URL)
        if (event.data.successData) {
          setLeadSourceDataFromWidget(event.data.successData);
          // Update desktop flag from parent-reported device type
          if (typeof event.data.successData.deviceType === 'string') {
            setIsDesktop(event.data.successData.deviceType === 'desktop');
          }
        }

        try {
          const url = new URL(newUrl);
          const pathSegments = url.pathname.split('/');
          
          // Update locale if it changed
          const newLocale = pathSegments[1]?.toLowerCase().split('-')[0];
          const isLocaleValid = newLocale && /^(en|fr|nl)$/.test(newLocale);
          if (isLocaleValid && newLocale !== locale) {
            setIsReady(false); // Hide widget immediately to prevent flicker
            setLocale(newLocale);
          }
        } catch (error) {
          console.error('Error parsing parent URL:', error);
        } finally {
          sendResizeMessage();
        }
      }
    };

    window.addEventListener('message', handleParentMessage);

    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, [sendResizeMessage, urlConfig, isFlowCompleted, locale]); 

  const shouldShowCircle = useCallback(() => {
    if (!urlConfig) return true;

    if (!parentUrl) return !urlConfig?.hideWidgetWhenDismissed;

    const onSpecialUrl = urlConfig?.showCircleOnUrls?.some((url: string) => parentUrl.startsWith(url));

    if (onSpecialUrl) {
      return true;
    }

    return !urlConfig?.hideWidgetWhenDismissed;
  }, [urlConfig, parentUrl]);

  // Function to proceed with normal dismissal
  const proceedWithDismissal = useCallback(() => {
    const showCircle = shouldShowCircle();

    window.parent.postMessage({
      type: 'WIDGET_DISMISSED',
      hideWidgetWhenDismissed: !showCircle,
    }, '*');

    if (showCircle) {
      setIsMinimized(true);
      setIsChatOpen(false);
    } else {
      setIsReady(false);
    }
  }, [shouldShowCircle]);

  // Function to update chat with quit reason
  const updateChatWithQuitReason = useCallback(async (reason: string) => {
    if (!chatId) return;

    try {
      const response = await fetch(`/api/conversations/${chatId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reasonQuit: reason
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update chat: ${response.status}`);
      }

      // Update local state
      setChatQuitReason(reason);
    } catch (error) {
      logger.error('Failed to update chat with quit reason:', error);
      throw error;
    }
  }, [chatId]);

  // --- Add state for notification visibility ---
  const [showNotification, setShowNotification] = useState(false);

  // --- Add effect to show notification after 5 seconds when minimized ---
  // Compute stackedDesktop flag for effects and rendering
  const stackedDesktop = isDesktop === true && !isMinimized && !isChatOpen;

  useEffect(() => {
    // Guard while quit modal is open: avoid unrelated UI state churn
    if (showQuitReasonModal) {
      return;
    }
    if (stackedDesktop && !isTransitioning) {
      // If we've never shown the delayed reveal, do it once on first stack
      if (!stackedRevealDoneRef.current) {
        setShowClosedTeaser(false);
        setShowNotification(false);
        const badgeTimer = setTimeout(() => setShowNotification(true), 1500);
        const revealTimer = setTimeout(() => {
          setShowClosedTeaser(true);
          stackedRevealDoneRef.current = true;
        }, 2500);
        return () => {
          clearTimeout(badgeTimer);
          clearTimeout(revealTimer);
        };
      }
      // Subsequent times within same session: show only circle (no stacked again)
      setShowNotification(true);
      setShowClosedTeaser(false);
      return;
    }

    // Minimized behavior retains existing 1s delayed badge
    if (isMinimized && !isTransitioning) {
      setShowNotification(false);
      const timer = setTimeout(() => setShowNotification(true), 1000);
      return () => clearTimeout(timer);
    }

    // Default: hide badge and ensure teaser is visible when not stacked
    setShowNotification(false);
    if (!stackedDesktop) setShowClosedTeaser(true);
  }, [stackedDesktop, isMinimized, isTransitioning, showQuitReasonModal]);

  // When opening the chat, ensure we jump to the bottom once
  useEffect(() => {
    if (isChatOpen) {
      scrollToBottomNow();
    }
  }, [isChatOpen, scrollToBottomNow]);

  // Session-based tracking: Only track when user actually interacts with widget

  // Track widget minimize impressions (when user explicitly minimizes)
  // The minimize tracking is handled by the minimizeChat function

  // Track workflow step changes (only after session has started)
  useEffect(() => {
    if (sessionStarted && sessionId && urlConfig && currentWorkflowStep) {
      // Update session with workflow step without counting as impression
      updateSessionWorkflowStep(sessionId, urlConfig, currentWorkflowStep);
    }
  }, [currentWorkflowStep, sessionStarted, sessionId, urlConfig, updateSessionWorkflowStep]);

  // Circular Button Component for minimized/stacked state
  // QuitReasonModal handlers
  const handleQuitModalCancel = useCallback(() => {
    setShowQuitReasonModal(false);
  }, []);

  const handleQuitModalSubmit = useCallback(async (finalReason: string) => {
    try {
      await updateChatWithQuitReason(finalReason);
    } catch (error) {
      logger.error('Failed to submit quit reason:', error);
    } finally {
      setShowQuitReasonModal(false);
      proceedWithDismissal();
    }
  }, [updateChatWithQuitReason, proceedWithDismissal]);

  const CircularButton = ({ forceChatIcon = false }: { forceChatIcon?: boolean }) => (
    <div className="fixed bottom-6 right-4 w-14 h-14 transition-all duration-300 ease-in-out z-[9999]">
      {/* Red notification badge - only show after delay */}
      {showNotification && (
        <div className="absolute -top-1 -right-0 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center border border-white animate-pulse z-10">
          <span className="text-[10px] font-bold text-white">1</span>
        </div>
      )}
      
      <div 
        className="
          w-full h-full
          bg-white 
          rounded-full 
          flex items-center justify-center 
          cursor-pointer 
          shadow-[0_2px_8px_rgba(0,0,0,0.2)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.25)] 
          transition-all duration-300 ease-out
          hover:scale-105 active:scale-95
        "
        onClick={() => {
          // Always ask parent (authoritative store) to clear dismissal state
          try {
            window.parent.postMessage({ type: 'CLEAR_WIDGET_DISMISSAL' }, '*');
          } catch (err) {
            // noop - best effort
          }
          // Best-effort local cleanup in iframe storage as well
          try { localStorage.removeItem('widgetDismissed'); } catch (_) {}
          
          const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
          
          setIsTransitioning(true);
          
          if (isMobile) {
            // Mobile: Longer delay for smoother transition
            setTimeout(() => {
              setIsMinimized(false);
              handleWidgetOpen();
              // Keep transitioning state a bit longer for mobile
              setTimeout(() => {
                setIsTransitioning(false);
              }, 200); // Additional 200ms for mobile transition completion
            }, 200); // Initial 200ms delay for mobile
          } else {
            // Desktop: Keep original timing for snappy response
            setTimeout(() => {
              setIsMinimized(false);
              handleWidgetOpen();
              setIsTransitioning(false);
            }, 150);
          }
        }}
      >
        {forceChatIcon ? (
          <div className="w-full h-full bg-white rounded-full flex items-center justify-center overflow-hidden p-1">
            <Image
              src="/logo_chat.svg"
              alt="Open chat"
              width={28}
              height={28}
              className="w-7 h-7 object-contain"
              priority
            />
          </div>
        ) : (
          chatFaviconUrl ? (
            <div className="w-full h-full bg-white rounded-full flex items-center justify-center overflow-hidden p-1">
              <Image 
                src={chatFaviconUrl} 
                alt="Agent Avatar" 
                width={40} 
                height={40} 
                className="rounded-full w-full h-full object-contain" 
                unoptimized={chatFaviconUrl.startsWith('http')} 
              />
            </div>
          ) : (
            <div className="w-full h-full bg-white rounded-full flex items-center justify-center">
              <svg 
                className="w-6 h-6 md:w-7 md:h-7 text-zinc-700" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z"/>
                <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z"/>
              </svg>
            </div>
          )
        )}
      </div>
    </div>
  );

  // Check if we're in inline mode
  const isInlineMode = urlConfig?.inline === true;

  // For inline mode, scroll to bottom when container is ready
  useEffect(() => {
    if (isInlineMode && messagesContainerRef.current && messages.length > 0) {
      scrollToBottomNow();
    }
  }, [isInlineMode, messages.length, scrollToBottomNow]);

  return (
    <>
      {!isBasicConfigLoaded || !urlConfig || typeof urlConfig !== 'object' ? (
        // Render nothing until basic config is loaded (fast ~50ms)
        null
      ) : isInlineMode ? (
        // Inline mode: Simple embedded layout
        // On mobile: Use same design as floating widget when open (full screen, no rounded corners)
        // On desktop: Fill container with rounded corners
        <div className={`bg-zinc-100 flex flex-col ${
          isDesktop === false
            ? 'fixed inset-0 w-screen h-[100dvh] z-[9999] pointer-events-auto'
            : 'w-full h-full overflow-hidden rounded-3xl'
        }`}>
          {/* Quit Reason Modal */}
          <QuitReasonModal
            open={showQuitReasonModal}
            title={dictionary?.quitReasonModal?.title || 'Weet je zeker dat je wilt sluiten?'}
            question={dictionary?.quitReasonModal?.question || 'We zouden graag willen weten waarom je weggaat:'}
            reasons={dictionary?.quitReasonModal?.reasons || {}}
            otherPlaceholder={dictionary?.quitReasonModal?.otherPlaceholder || 'Vertel ons meer...'}
            stayLabel={dictionary?.quitReasonModal?.stay || 'Blijven'}
            closeLabel={dictionary?.quitReasonModal?.continueAnyway || 'Sluiten'}
            onCancel={handleQuitModalCancel}
            onSubmit={handleQuitModalSubmit}
          />

          {/* Header - Fixed height with proper background */}
          <div className="relative flex-shrink-0 px-3 py-4 min-h-[80px]">
            {/* Background Layer */}
            {urlConfig?.headerStyling?.type === 'corner' ? (
              <div
                className="absolute inset-0"
                style={{
                  backgroundColor: urlConfig?.headerStyling?.backgroundColor || '#BD1622',
                  clipPath: 'polygon(0 0, 100% 0, 100% 30%, 0 80%)'
                }}
              ></div>
            ) : urlConfig?.headerStyling?.type === 'gradient' ? (
              <div
                className="absolute inset-0 header-background-layer overflow-hidden"
                style={{
                  background: urlConfig?.headerStyling?.background || `linear-gradient(to bottom, ${chatBackgroundColor} 20%, transparent 100%)`,
                  backgroundBlendMode: urlConfig?.headerStyling?.backgroundBlendMode || 'normal'
                }}
              ></div>
            ) : (
              <div
                className="absolute inset-0 header-background-layer overflow-hidden"
                style={{
                  background: `linear-gradient(to bottom, ${chatBackgroundColor} 20%, transparent 100%)`,
                }}
              ></div>
            )}

            {/* Header Content */}
            <div className="relative z-10 flex items-center gap-2">
              {urlConfig?.swapLogoWithAI ? (
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <span className="text-base font-semibold text-[#BD1622]">
                    {(urlConfig?.aiChipTextOverrideKey && dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey]) || dictionary?.sellingForm?.aiChipText || 'AI'}
                  </span>
                </div>
              ) : (
                chatFaviconUrl && (
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden">
                    <Image
                      src={chatFaviconUrl}
                      alt="Agent Avatar"
                      width={40}
                      height={40}
                      className="rounded-full w-full h-full object-contain"
                      unoptimized={chatFaviconUrl.startsWith('http')}
                    />
                  </div>
                )
              )}
              <div>
                <h3
                  className="text-lg font-semibold text-gray-900"
                  style={{
                    ...chatTextStyle,
                    color: urlConfig?.headerStyling?.textColor || chatTextStyle?.color
                  }}
                >
                  {urlConfig?.headerTitleOverrideKey && dictionary?.sellingForm?.[urlConfig.headerTitleOverrideKey]
                    ? dictionary.sellingForm[urlConfig.headerTitleOverrideKey]
                    : dictionary?.sellingForm?.headerTitle || 'Value my home...'}
                </h3>
              </div>
            </div>

            {/* Progress bar */}
            {(progressPercent > 0 || currentWorkflowStep === AVM_STEPS[0]) && (
              <div className="relative z-10 mt-4 w-full flex items-center gap-3">
                <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${progressPercent}%`,
                      backgroundColor: urlConfig?.styling?.backgroundColor || '#18181b'
                    }}
                  ></div>
                </div>
                {currentStepNumber > 0 && (
                  <span className="text-xs text-zinc-600 font-medium whitespace-nowrap bg-white px-2 py-0.5 rounded-full border border-zinc-200">
                    {dictionary?.chat?.progressStepLabel || 'Step'} {currentStepNumber} / {totalDisplaySteps}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Messages Container - Fixed height for scrolling */}
          <div
            ref={messagesContainerRef}
            className={`${
              isDesktop === false ? 'flex-1 min-h-0' : 'h-[575px]'
            } overflow-y-auto px-2 pt-8 space-y-6 bg-zinc-100 pb-16`}
          >
            <style jsx>{`
              .typing-fade-in {
                opacity: 0;
                animation: fadeIn 300ms ease-out forwards;
              }
              .typing-fade-in span {
                animation: bounceUp 1s cubic-bezier(0.28, 0.84, 0.42, 1) infinite;
              }
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes bounceUp {
                0%, 80%, 100% { transform: translateY(0); }
                40% { transform: translateY(-6px); }
              }
            `}</style>
            {(() => {
              return messages.map((message, index) => {
                const isFormMessage = message.role === 'assistant' && message.content?.includes('|SHOW_SELLING_FORM');
                const isReasoningMessage = message.role === 'assistant' && message.content === '|SHOW_REASONING_LOADER';
                const isTypingMessage = message.role === 'assistant' && message.content === '|ASSISTANT_TYPING';

                const assistantBaseClasses = 'bg-white text-black';
                const assistantLayout = isFormMessage
                  ? `${isInlineMode && isDesktop ? 'max-w-[70%]' : 'max-w-[90%]'} w-full`
                  : `${isInlineMode && isDesktop ? 'max-w-[70%]' : 'max-w-[90%]'} mr-2`;

                const isLastMessage = index === messages.length - 1;

                return (
                  <div key={message.id || index} ref={isLastMessage ? lastMessageRef : null}>
                    <div className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                      <div
                        className={`px-3 py-2 text-base shadow-sm ${
                          message.role === 'user'
                            ? `bg-zinc-800 text-white ${isInlineMode && isDesktop ? 'max-w-[70%]' : 'max-w-[90%]'} ml-2 rounded-2xl rounded-br-md`
                            : `${assistantBaseClasses} ${assistantLayout} rounded-2xl rounded-bl-md`
                        }`}
                      >
                        {isFormMessage ? (
                          renderSellingForm(message)
                        ) : isReasoningMessage ? (
                          <ReasoningLoader
                            dictionary={dictionary}
                            city={message.properties?.city || ''}
                            propertyType={message.properties?.propertyType || ''}
                            livingArea={message.properties?.livingArea || 0}
                            bedrooms={message.properties?.bedrooms ?? 0}
                            bathrooms={message.properties?.bathrooms ?? 0}
                            plotArea={message.properties?.plotArea}
                            condition={sellingState.propertyInfo?.condition || ''}
                            epcValue={sellingState.propertyInfo?.epcValue || ''}
                            asbestos={sellingState.propertyInfo?.asbestos || ''}
                            finishLevel={sellingState.propertyInfo?.finishLevel || ''}
                            additionalDetails={message.properties?.additionalDetails}
                            currentAvmWorkflowStep={currentWorkflowStep}
                          />
                        ) : isTypingMessage ? (
                          <div className="flex items-center gap-1 typing-fade-in p-1">
                            <span className="inline-block w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                            <span className="inline-block w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                            <span className="inline-block w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                          </div>
                        ) : (
                          <div dangerouslySetInnerHTML={{
                            __html: formatMessage(message.content)
                          }} />
                        )}
                      </div>
                      {message.role === 'assistant' && !isReasoningMessage && !isTypingMessage && index === messages.length - 1 && (
                        <div className="mt-1 px-2">
                          <p className="text-[10px] text-zinc-500">
                            {dictionary?.chat?.agentTitle || 'Real Estate Expert'} • {dictionary?.time?.justNow || 'Just now'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Footer - positioned like floating widget on mobile */}
          <div className={`flex justify-center ${
            isDesktop === false
              ? 'pointer-events-none absolute bottom-2 left-0 right-0'
              : 'pb-2'
          }`}>
            <a
              href={`https://koqoon.be/${(locale || 'nl')}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-[9px] text-zinc-500 hover:text-zinc-800 hover:underline bg-gray-100 px-2 py-1 rounded-full ${
                isDesktop === false ? 'pointer-events-auto' : ''
              }`}
            >
              {dictionary?.chat?.poweredByKoqoon || 'Powered by Koqoon'}
            </a>
          </div>
        </div>
      ) : (
        // Floating mode (existing logic unchanged)
        <>
          {isMinimized && !isTransitioning ? (
            <CircularButton />
          ) : (
            <>
              {/* Chat Window - WhatsApp style */}
              {isChatOpen ? (
              // --- Apply mobile full-screen styles first, then override for md+ ---
                <div
                  className={`
                    fixed inset-0 w-screen h-[100dvh] z-[9999] pointer-events-auto
                    transition-all duration-300 ease-out
                    ${isChatOpen
                  ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 scale-95 translate-y-8 pointer-events-none'}
                    md:inset-auto md:bottom-6 md:right-6 md:w-[90%] md:h-[95%] md:max-w-[400px] md:max-h-[800px]'
                  `}
                >
                  {/* --- Adjust close button position for mobile vs desktop --- */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      
                      // Check if we should show quit reason modal
                      const shouldShow = shouldShowQuitReasonModal();
                      
                      if (shouldShow) {
                        setShowQuitReasonModal(true);
                        return; // Exit early, don't proceed with dismissal
                      }

                      // Normal minimize logic (existing behavior)
                      minimizeChat();
                    }}
                    className="absolute top-3 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-color)] transition-colors hover:bg-gray-100 hover:text-gray-700"
                    style={{
                      '--text-color': urlConfig?.headerStyling?.textColor || chatTextStyle?.color || '#4b5563'
                    } as React.CSSProperties}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>

                  {/* --- Inner Container: Remove overflow-hidden --- */}
                  <div
                    ref={chatRef}
                    className="relative w-full h-full bg-zinc-100 flex flex-col pointer-events-auto font-sans border border-zinc-200
                                md:rounded-xl md:shadow-xl md:overflow-hidden"
                    style={urlConfig?.styling}
                  >
                    {/* Quit Reason Modal - positioned within widget */}
                    <QuitReasonModal
                      open={showQuitReasonModal}
                      title={dictionary?.quitReasonModal?.title || 'Weet je zeker dat je wilt sluiten?'}
                      question={dictionary?.quitReasonModal?.question || 'We zouden graag willen weten waarom je weggaat:'}
                      reasons={dictionary?.quitReasonModal?.reasons || {}}
                      otherPlaceholder={dictionary?.quitReasonModal?.otherPlaceholder || 'Vertel ons meer...'}
                      stayLabel={dictionary?.quitReasonModal?.stay || 'Blijven'}
                      closeLabel={dictionary?.quitReasonModal?.continueAnyway || 'Sluiten'}
                      onCancel={handleQuitModalCancel}
                      onSubmit={handleQuitModalSubmit}
                    />
                    {/* --- Header: Adjust padding/rounding for mobile --- */}
                    <div 
                      className="relative flex flex-col px-3 py-4 
                                  md:pt-4 md:pb-4 md:rounded-t-xl"
                    >
                      {/* New Background Layer - Conditionally rendered */}
                      {urlConfig?.headerStyling?.type === 'corner' ? (
                        <div
                          className="absolute inset-0"
                          style={{
                            backgroundColor: urlConfig?.headerStyling?.backgroundColor || '#BD1622',
                            clipPath: 'polygon(0 0, 100% 0, 100% 30%, 0 80%)'
                          }}
                        ></div>
                      ) : urlConfig?.headerStyling?.type === 'gradient' ? (
                        <div 
                          className="absolute inset-0 header-background-layer overflow-hidden"
                          style={{
                            background: urlConfig?.headerStyling?.background || `linear-gradient(to bottom, ${chatBackgroundColor} 20%, transparent 100%)`,
                            backgroundBlendMode: urlConfig?.headerStyling?.backgroundBlendMode || 'normal'
                          }}
                        ></div>
                      ) : (
                        <div 
                          className="absolute inset-0 header-background-layer overflow-hidden"
                          style={{
                            background: `linear-gradient(to bottom, ${chatBackgroundColor} 20%, transparent 100%)`,
                          }}
                        ></div>
                      )}

                      {/* Content goes here, after the background layer */}
                      {/* Add z-10 to ensure content stays above the absolute background */}
                      <div className="relative z-10 flex items-center gap-2"> 
                        {urlConfig?.swapLogoWithAI ? (
                          <div className="w-10 h-10 md:w-8 md:h-8 bg-white rounded-full flex items-center justify-center">
                            <span className="text-base md:text-sm font-semibold text-[#BD1622]">
                              {(urlConfig?.aiChipTextOverrideKey && dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey]) || dictionary?.sellingForm?.aiChipText || 'AI'}
                            </span>
                          </div>
                        ) : (
                          chatFaviconUrl && (
                            <div className="w-10 h-10 md:w-8 md:h-8 bg-white rounded-full flex items-center justify-center overflow-hidden">
                              <Image 
                                src={chatFaviconUrl} // Use state directly
                                alt="Agent Avatar" 
                                width={40} 
                                height={40} 
                                className="rounded-full w-full h-full object-contain" // Ensure image fits within wrapper
                                unoptimized={chatFaviconUrl.startsWith('http')} // Disable optimization for external URLs
                              />
                            </div>
                          )
                        )}
                        <div>
                          <h3
                            className="text-lg font-semibold text-gray-900 md:text-base"
                            style={{
                              ...chatTextStyle,
                              color: urlConfig?.headerStyling?.textColor || chatTextStyle?.color
                            }}
                          >
                            {urlConfig?.headerTitleOverrideKey && dictionary?.sellingForm?.[urlConfig.headerTitleOverrideKey]
                              ? dictionary.sellingForm[urlConfig.headerTitleOverrideKey]
                              : dictionary?.sellingForm?.headerTitle || 'Value my home...'}
                          </h3>
                        </div>
                      </div>

                      {/* Progress bar starts here */}
                      {(progressPercent > 0 || currentWorkflowStep === AVM_STEPS[0]) && (
                        <div className="relative z-10 mt-4 w-full flex items-center gap-3">
                          <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-300"
                              style={{ 
                                width: `${progressPercent}%`,
                                backgroundColor: urlConfig?.styling?.backgroundColor || '#18181b'
                              }}
                            ></div>
                          </div>
                          {currentStepNumber > 0 && (
                            <span className="text-xs text-zinc-600 font-medium whitespace-nowrap bg-white px-2 py-0.5 rounded-full border border-zinc-200">
                              {dictionary?.chat?.progressStepLabel || 'Step'} {currentStepNumber} / {totalDisplaySteps}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div
                      ref={messagesContainerRef}
                      data-avm-messages="true"
                      className="flex-1 min-h-0 overflow-y-auto px-2 pt-8 pb-16 space-y-6 md:space-y-4 md:p-4 md:pb-16"
                      style={urlConfig?.styling}
                    >
                      <style jsx>{`
                        .typing-fade-in {
                          opacity: 0;
                          animation: fadeIn 300ms ease-out forwards;
                        }
                        .typing-fade-in span {
                          animation: bounceUp 1s cubic-bezier(0.28, 0.84, 0.42, 1) infinite;
                        }
                        @keyframes fadeIn {
                          from { opacity: 0; }
                          to { opacity: 1; }
                        }
                        @keyframes bounceUp {
                          0%, 80%, 100% { transform: translateY(0); }
                          40% { transform: translateY(-6px); }
                        }
                      `}</style>
                      {(() => {
                        const lastAssistantMessageIndex = messages.map(m => m.role).lastIndexOf('assistant');
                        
                        return messages.map((message, index) => {
                          const isFormMessage = message.role === 'assistant' && message.content?.includes('|SHOW_SELLING_FORM');
                          // --- Check for special assistant flags --- 
                          const isReasoningMessage = message.role === 'assistant' && message.content === '|SHOW_REASONING_LOADER';
                          const isTypingMessage = message.role === 'assistant' && message.content === '|ASSISTANT_TYPING';

                          const assistantBaseClasses = 'bg-white text-black';
                          const assistantMobileLayout = isFormMessage ? 'max-w-[90%] w-full' : 'max-w-[90%] mr-2';
                          const assistantDesktopLayout = 'md:max-w-[85%] md:mr-4';
                            
                          const isLastMessage = index === messages.length - 1;
                            
                          return (
                            // --- Attach ref conditionally, use stable key --- 
                            <div key={message.id || index} ref={isLastMessage ? lastMessageRef : null}>
                              <div className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                                <div
                                  className={`px-3 md:px-3 py-2 md:py-2 text-base md:text-sm shadow-sm ${
                                    message.role === 'user'
                                      ? 'bg-zinc-800 text-white max-w-[90%] md:max-w-[85%] ml-2 md:ml-4 rounded-2xl rounded-br-md'
                                      : `${assistantBaseClasses} ${assistantMobileLayout} ${assistantDesktopLayout} rounded-2xl rounded-bl-md`
                                  }`}
                                >
                                  {isFormMessage ? (
                                    renderSellingForm(message)
                                  ) : (
                                  // --- Render ReasoningLoader if it's the reasoning message --- 
                                    isReasoningMessage ? (
                                      <ReasoningLoader 
                                        dictionary={dictionary} 
                                        // --- Pass data from message properties --- 
                                        city={message.properties?.city || ''}
                                        propertyType={message.properties?.propertyType || ''}
                                        livingArea={message.properties?.livingArea || 0}
                                        // --- Pass new properties with defaults --- 
                                        bedrooms={message.properties?.bedrooms ?? 0}
                                        bathrooms={message.properties?.bathrooms ?? 0}
                                        plotArea={message.properties?.plotArea} // plotArea is already optional
                                        // --- Pass condition and epcValue from sellingState (still needed for loader text) ---
                                        condition={sellingState.propertyInfo?.condition || ''}
                                        epcValue={sellingState.propertyInfo?.epcValue || ''}
                                        asbestos={sellingState.propertyInfo?.asbestos || ''}
                                        finishLevel={sellingState.propertyInfo?.finishLevel || ''}
                                        // --- Pass additional details DIRECTLY from the message properties ---
                                        additionalDetails={message.properties?.additionalDetails}
                                        // --- Pass the overall current workflow step ---
                                        currentAvmWorkflowStep={currentWorkflowStep}
                                      />
                                    ) : isTypingMessage ? (
                                      <div className="flex items-center gap-1 typing-fade-in p-1">
                                        <span className="inline-block w-2 h-2 md:w-1.5 md:h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                        <span className="inline-block w-2 h-2 md:w-1.5 md:h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                        <span className="inline-block w-2 h-2 md:w-1.5 md:h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                      </div>
                                    ) : (
                                    // --- Otherwise, render standard text message --- 
                                      <div dangerouslySetInnerHTML={{ 
                                        __html: formatMessage(message.content)
                                      }} />
                                    )
                                  )}
                                </div>
                                {message.role === 'assistant' && !isReasoningMessage && !isTypingMessage && index === messages.length - 1 && (
                                  <div className="mt-1 px-2">
                                    <p className="text-[10px] text-zinc-500">
                                      {dictionary?.chat?.agentTitle || 'Real Estate Expert'} • {dictionary?.time?.justNow || 'Just now'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    {/* Bottom-centered footer: Powered by Koqoon */}
                    <div className="pointer-events-none absolute bottom-2 left-0 right-0 flex justify-center">
                      <a
                        href={`https://koqoon.be/${(locale || 'nl')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pointer-events-auto text-[9px] text-zinc-500 hover:text-zinc-800 hover:underline bg-gray-100 px-2 py-1 rounded-full"
                      >
                        {dictionary?.chat?.poweredByKoqoon || 'Powered by Koqoon'}
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Mobile backdrop blur overlay - only show under the widget on mobile when closed */}
                  {!isDesktop && !isMinimized && (
                    <div 
                      className="fixed z-30 transition-all duration-300 ease-in-out"
                      style={{ 
                        bottom: '0',
                        left: '2.5%',
                        right: '2.5%',
                        height: '140px', // Approximate height of the closed widget
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                      }}
                    />
                  )}
                  
                  <div
                    className={`fixed z-40 pointer-events-auto bottom-12 transition-all duration-300 ease-in-out hover:scale-[1.025] ${
                      isMinimized 
                        ? `${isDesktop ? 'w-16 h-16 bottom-6 right-2' :'w-14 h-14 bottom-8 right-2'} max-w-none rounded-full` 
                        : isDesktop 
                          ? 'w-auto max-w-[350px] right-6 translate-x-0 left-auto'
                          : 'mx-auto max-w-[95%] w-full'
                    } ${isTransitioning ? 'opacity-0 scale-95' : ''}`}
                    style={{ bottom: undefined }}
                  >
                    {/* Top Component - Positioned directly above the widget */}
                    {urlConfig?.top && (
                      <div className="absolute bottom-full left-0 right-0 mb-2">
                        {React.createElement(urlConfig.top, {
                          onOpenChat: () => handleWidgetOpen()
                        })}
                      </div>
                    )}

                    {/* Left Component - Positioned directly to the left of the widget */}
                    {urlConfig?.left && (
                      <div className="absolute right-full top-0 bottom-0 mr-2 flex items-center">
                        {React.createElement(urlConfig.left, {
                          onOpenChat: () => handleWidgetOpen()
                        })}
                      </div>
                    )}

                    <div 
                      className={`${isDesktop ? 'pt-6 pb-8' : 'pt-5 pb-7'} relative rounded-3xl bg-white shadow-xl px-2 flex flex-col gap-3 cursor-pointer border-2 border-white transition-opacity duration-300 opacity-100`}
                      onClick={() => handleWidgetOpen()}
                      style={urlConfig?.closedWidgetStyling ?? urlConfig?.styling ?? {}}
                    >
                      {/* AI Chip with shimmer effect - bottom right (vAI version) */}
                      {abTestVersion === 'vAI' && (
                        <div className="absolute bottom-3 right-3 z-20">
                          <div className="bg-white text-base font-semibold px-3 py-1 rounded-2xl shadow-lg">
                            <span 
                              className="bg-clip-text text-transparent animate-shimmer bg-[length:200%_50%]"
                              style={{
                                backgroundImage: `linear-gradient(to right, ${
                                  ((urlConfig?.closedWidgetStyling ?? {}) as any)?.color === '#000000' || ((urlConfig?.closedWidgetStyling ?? {}) as any)?.color === 'black' 
                                    ? '#000000' 
                                    : ((urlConfig?.closedWidgetStyling ?? {}) as any)?.backgroundColor || '#000'
                                }, white, ${
                                  ((urlConfig?.closedWidgetStyling ?? {}) as any)?.color === '#000000' || ((urlConfig?.closedWidgetStyling ?? {}) as any)?.color === 'black' 
                                    ? '#000000' 
                                    : ((urlConfig?.closedWidgetStyling ?? {}) as any)?.backgroundColor || '#000'
                                })`
                              }}
                            >
                              AI
                            </span>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();

                          
                          
                          // Check if we should show quit reason modal
                          const shouldShow = shouldShowQuitReasonModal();
                          
                          if (shouldShow) {
                            setShowQuitReasonModal(true);
                            return; // Exit early, don't proceed with dismissal
                          }

                          // Normal dismiss logic (existing behavior)
                          proceedWithDismissal();
                        }}
                        className={`absolute top-3 right-3 z-20 flex h-6 w-6 items-center justify-center rounded-lg text-[var(--text-color)] transition-colors hover:bg-gray-100 hover:text-gray-700 ${
                          urlConfig?.closedWidgetCloseButtonClassName || ''
                        }`}
                        style={{
                          '--text-color': ((urlConfig?.closedWidgetStyling ?? urlConfig?.styling ?? {}) as any)?.color || '#111827'
                        } as React.CSSProperties}
                        aria-label="Close widget"
                      >
                        <XMarkIcon className={`${urlConfig?.closedWidgetCloseIconClassName || 'h-4 w-4'}`} />
                      </button>

                      {/* Arrow button - bottom right (vArrow version) */}
                      {abTestVersion === 'vArrow' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWidgetOpen();
                          }}
                          className={`absolute bottom-3 right-3 z-20 flex h-6 w-6 items-center justify-center rounded-lg text-[var(--text-color)] transition-colors hover:bg-gray-100 hover:text-gray-700 ${
                            urlConfig?.closedWidgetCloseButtonClassName || ''
                          }`}
                          style={{
                            '--text-color': ((urlConfig?.closedWidgetStyling ?? urlConfig?.styling ?? {}) as any)?.color || '#111827'
                          } as React.CSSProperties}
                          aria-label="Open widget"
                        >
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </button>
                      )}
                      
                      <div className="flex items-center gap-3 pr-10">
                        {urlConfig?.swapLogoWithAI ? (
                          <div className="min-w-10 min-h-10 w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden">
                            <span className="text-base font-semibold" style={{ color: (urlConfig?.closedWidgetStyling ?? {})?.backgroundColor || '#000' }}>
                              {(urlConfig?.aiChipTextOverrideKey && dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey])
                                ? dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey]
                                : (dictionary?.sellingForm?.aiChipText || 'AI')}
                            </span>
                          </div>
                        ) : chatFaviconUrl ? (
                          <div className="min-w-10 min-h-10 w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm">
                            <Image 
                              src={chatFaviconUrl}
                              alt="Agent Avatar"
                              width={40}
                              height={40}
                              className="rounded-full w-full h-full object-contain"
                              unoptimized={chatFaviconUrl.startsWith('http')}
                            />
                          </div>
                        ) : (
                          <div className="min-w-10 min-h-10 w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden">
                            <span className="text-sm font-semibold" style={{ color: (urlConfig?.closedWidgetStyling ?? {})?.backgroundColor || '#000' }}>
                              {(urlConfig?.aiChipTextOverrideKey && dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey])
                                ? dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey]
                                : (dictionary?.sellingForm?.aiChipText || 'AI')}
                            </span>
                          </div>
                        )}
                        <div className="flex flex-col">
                          <p
                            className={`${isDesktop ? 'text-base leading-5 pr-1' : 'text-base leading-5'} font-semibold text-gray-900`}
                            style={{ color: (urlConfig?.closedWidgetStyling ?? urlConfig?.styling ?? {})?.color }}
                          >
                            {urlConfig?.widgetTitleOverrideKey && dictionary?.sellingForm?.[urlConfig.widgetTitleOverrideKey]
                              ? dictionary.sellingForm[urlConfig.widgetTitleOverrideKey]
                              : dictionary?.sellingForm?.CTAMessage || 'How much is my home worth?'}
                          </p>
                        </div>
                      </div>

                      {/* Default now: single row (logo left, title right). Remove old arrow/AI rows */}
                    </div>
                  </div>
                  {/* Also render the circular launcher on desktop when minimized */}
                  {(isDesktop === true && isMinimized) && (
                    <CircularButton forceChatIcon={true} />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

export default AvmAgent;